import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, finalize, map, Observable, of } from 'rxjs';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { RoleService } from '../../../services/rbackservices/role.service';
import { environment } from '../../../../../environments/environment';
import { ApiCallsService } from '../../../services/api-calls.service';
import { ToastService } from '../../../services/toast.service';
import { KnowledgeBaseComponent } from './knowledgebase/knowledge-base.component';
import Swal from 'sweetalert2';

// ============================================================
// Form / ModalForm types (unchanged from before)
// ============================================================
interface OptionMapping {
  id: string;
  name: string;
}
interface MappingEntry {
  key: string;   // raw stored value, e.g. "N"
  value: string; // display value, e.g. "New"
}
interface MappingConfig {
  mappingUrl: string;
  mappingKey: string;
  mappingValue: string;
  payload?: any;
  payloadStr?: string;
  json: string | Record<string, string>;
}
interface ExtraOption {
  id: string;
  name: string;
}

interface FieldConfig {
  id: string;
  placeholder: string;
  type: ColumnType;
  class: string;
  dropdown: boolean;
  apiUrl: string;
  payload?: any;
  payloadStr?: string;
  optionKey: string;
  option_mapping: OptionMapping;
  extraOption?: ExtraOption | null;
  matchKey?: string;
  regex: string;
  regexMessage: string;
  required: boolean;
  readonly: boolean;
  view: boolean;
  role?: string[];
  options: StaticOption[];
  optionsMode: OptionsMode;
  expanded: boolean;
  conversionType?: string;
  bitCount?: number;
  bitLabels?: string[];
  bitLabelsStr?: string;
  minBase?: number | string;
  maxBase?: number | string;
  baseValue?: number | string;
  bitDirection?: 'ltr' | 'rtl';
  minDate?: string;
  maxDate?: string;
  monthYear?: boolean;
  fillFromKey?: string;
  conditionEnabled?: boolean;
  conditionKey?: string;
  conditionOperator?: string;
  conditionValue?: string;
  conditions?: ConditionRule[];
  conditionLogic?: 'AND' | 'OR';
}

export interface ConditionRule {
  key: string;
  operator: string;
  value: string;
  logic?: 'AND' | 'OR';
}

interface ModalFormConfig {
  modalTitle: string;
  modalSize: 'sm' | 'md' | 'lg' | 'xl';
  fields: FieldConfig[];
}

// ============================================================
// Table types (nested document shape)
// ============================================================
type ColumnType = 'text' | 'number' | 'date' | 'dropdownUrl' | 'dropdownList' | 'bitcase';
type ClickType = 'redirect' | 'popup' | 'openForm';
type OptionsMode = 'none' | 'static' | 'api';

interface StaticOption {
  id: string;
  name: string;
}

interface RoleOption {
  _id: string;
  name: string;
  type: string;
  login_mode: string;
}

interface FilterFieldConfig {
  title: string;
  key: string;
  type: 'dropdownUrl' | 'dropdownList' | 'text' | 'date' | 'select' | 'month-year' | 'datetime';
  url: string;
  placeholder?: string;
  col?: string;
  required?: boolean;
  StaticPayload?: any;
  staticPayloadStr?: string;
  options?: any[];
  valueKey?: string;
  labelKey?: string;
  expanded?: boolean;
}

interface OptionsConfig {
  url: string;
  key: string;
  value: string;
  matchKey?: string;
  list: StaticOption[];
  payload?: any;
  payloadStr?: string;
  extraOption?: ExtraOption | null;
}
type ColumnBoolFlag = 'show' | 'add' | 'edit' | 'view' | 'readonly' | 'readonly_add' | 'readonly_edit' | 'required' | 'display' | 'download' | 'total' | 'ignore_value';

interface PopupConfig {
  title: string;
  size: 'sm' | 'md' | 'lg' | 'xl';
  contentUrl: string;
  payload?: any;
  payloadStr?: string;
}

interface ClickConfig {
  type: ClickType;
  redirectUrl?: string;
  payload?: Record<string, any>;
  payloadStr?: string;
  popup?: PopupConfig;
  apiKey?: string;
  fields?: FieldConfig[];
  rowKeys?: string[];
  rowKeysStr?: string;
  confirmAction?: boolean;
  warningMessage?: string;
}

/** One column's config — becomes the value at list.columns[key] on save */
interface ColumnConfig {
  _id?: string;
  key: string;
  title: string;
  titleOverrides?: Record<string, string>;
  Order_By?: number | string;
  show: boolean;
  add: boolean;
  edit: boolean;
  view: boolean;
  readonly?: boolean;
  readonly_add: boolean;
  readonly_edit: boolean;
  required: boolean;
  display: boolean;
  class: string;
  regex: string;
  role_add: string[];
  role_edit: string[];
  role_readonly: string[];
  role_readonly_add: string[];
  role_readonly_edit: string[];
  regexMessage: string;
  type: ColumnType;
  formula?: string;
  rowspan?: boolean | string | string[];
  row_formula?: string;
  groupBy?: string | string[];
  valign?: 'top' | 'middle' | 'bottom';
  position?: 'top' | 'middle' | 'bottom';
  repeatOnEveryPage?: boolean;
  repeat_on_page?: boolean;
  showOnEveryPage?: boolean;
  click: ClickConfig | null;
  options: OptionsConfig | null;
  mapping: MappingConfig | null;
  download: boolean;
  total?: boolean;
  role: string[];
  status: 'Y' | 'N';
  ignore_value?: boolean;
  value?: string;
  conversionType?: string;
  bitCount?: number;
  bitLabels?: string[];
  bitLabelsStr?: string;
  minBase?: number | string;
  maxBase?: number | string;
  baseValue?: number | string;
  bitDirection?: 'ltr' | 'rtl';
  minDate?: string;
  maxDate?: string;
  monthYear?: boolean;
}

/** UI-only wrapper — payload plus editor state */
interface TableColumnRow {
  payload: ColumnConfig;
  optionsMode: OptionsMode;
  expanded: boolean;
  mappingEntries: MappingEntry[];
  titleOverrideEnabled: boolean;
  titleOverrideGroups: TitleOverrideGroup[];
}
interface TitleOverrideGroup {
  roles: string[];  // role "type" values sharing this title
  title: string;    // the display title applied to all roles in this group
}
interface TableMeta {
  title: string;
  listTitle: string;
  dataUrl: string;
  dataPayload?: any;
  dataPayloadStr?: string;
  add: boolean;
  edit: boolean;
  addUrl: string;
  addPayload?: any;
  addPayloadStr?: string;
  editUrl: string;
  editPayload?: any;
  editPayloadStr?: string;
  add_routeurl: string;   // NEW
  edit_routeurl: string;  // NEW
  csv: boolean;
  pdf: boolean;
  load_after_filter: boolean;
  pagination: boolean;
  enableCheckbox: boolean;
  is_reprint: boolean;
  tableActionType: string;
  tableActionLabel: string;
  tableActionBadge: string;
  filterEnabled: boolean;
  filterFlags: Record<string, boolean>;
  filterConfig: any[];
}

const COLUMN_DEFAULTS: Record<string, any> = {
  title: '',
  titleOverrides: {},
  Order_By: '',
  show: true,
  add: false,
  edit: false,
  view: true,
  readonly: false,
  readonly_add: false,
  readonly_edit: false,
  required: false,
  display: true,
  class: '',
  regex: '',
  regexMessage: '',
  type: 'text',
  formula: '',
  rowspan: false,
  row_formula: '',
  groupBy: '',
  valign: 'middle',
  position: 'middle',
  repeatOnEveryPage: true,
  click: null,
  options: null,
  mapping: null,
  download: true,
  total: false,
  role: [],
  role_add: [],
  role_edit: [],
  role_readonly: [],
  role_readonly_add: [],
  role_readonly_edit: [],
  status: 'Y',
  ignore_value: false,
  value: '',
  minDate: '',
  maxDate: '',
  monthYear: false
};

interface FieldInfoMap {
  title: string;
  listTitle: string;
  dataUrl: string;
  dataPayload: string;
  addUrl: string;
  addPayload: string;
  editUrl: string;
  editPayload: string;
  csv: string;
  pdf: string;
  load_after_filter: string;
  pagination: string;
  enableCheckbox: string;
  filterEnabled: string;
  filterField: string;
  colKey: string;
  class: string;
  colTitle: string;
  Order_By: string;
  show: string;
  add: string;
  edit: string;
  view: string;
  readonly: string;
  readonly_add: string;
  readonly_edit: string;
  required: string;
  display: string;
  download: string;
  total: string;
  role: string;
  roleAdd: string;
  roleEdit: string;
  roleReadonly: string;
  roleReadonlyAdd: string;
  roleReadonlyEdit: string;
  colType: string;
  formula: string;
  regex: string;
  regexMessage: string;
  optionsMode: string;
  optionsApi: string;
  optionsPayload: string;
  optionsKey: string;
  optionsValue: string;
  mapping: string;
  mappingPayload: string;
  click: string;
  clickRedirect: string;
  clickPopup: string;
  clickPayload: string;
  addRouteUrl: string;
  editRouteUrl: string;
  minDate: string;
  maxDate: string;
  monthYear: string;
  [key: string]: string;
}

const FIELD_INFO: FieldInfoMap = {
  title: 'Display name for this component, shown in navigation and page headers',
  listTitle: 'Header text shown above the data table grid',
  dataUrl: 'API endpoint used to fetch the table data list',
  dataPayload: 'Static JSON payload merged with table request (pagination, sort, search)',
  addUrl: 'API endpoint called when submitting the Add record form',
  addPayload: 'Static JSON payload merged and sent with Add form submissions',
  editUrl: 'API endpoint called when submitting the Edit record form',
  editPayload: 'Static JSON payload merged and sent with Edit form submissions',
  csv: 'Enable CSV export button for this table',
  pdf: 'Enable PDF export button for this table',
  load_after_filter: 'Controls data loading: By Default loads rows immediately; After Filter waits until user applies a filter',
  pagination: 'Enable or disable server pagination for this table. Default is True (Y). Set to False (N) for full client-side tables like Sites.',
  enableCheckbox: 'Enable row selection checkboxes for picking specific rows (e.g. reprint receipt)',
  filterEnabled: 'Show the filter bar above the table',
  filterField: 'Target column key to filter when this filter changes',
  colKey: 'Database or API field key (e.g. name, status, created_at)',
  colTitle: 'Display text shown in the table header',
  Order_By: 'Order of appearance in Add/Edit modal forms (1, 2, 3...). Overrides the column grid position in forms.',
  class: 'CSS classes applied to this column cell or form input',
  show: 'Show this column in table, Add form, Edit form, and exports',
  add: 'Include this column in the Add form',
  edit: 'Include this column in the Edit form',
  view: 'Show this column field in the form UI (set false to pass value silently)',
  readonly: 'Field cannot be edited by the user (sent read-only in form)',
  readonly_add: 'Field cannot be edited in Add form modal (read-only for Add)',
  readonly_edit: 'Field cannot be edited in Edit form modal (read-only for Edit)',
  required: 'Field is mandatory before submitting form',
  display: 'Show column visually in table (set false to keep in data but hide UI)',
  download: 'Include this column in CSV and PDF exports',
  total: 'Calculate and display total sum in PDF and CSV exports for this column',
  ignore_value: 'When Y, bypasses database row values and shows the static value (e.g. SET/GET) for all rows',
  role: 'Roles not allowed to see or use this column. Selected via searchable multi-select dropdown.',
  roleAdd: 'Roles not allowed to see this column in Add form. Selected via searchable multi-select dropdown.',
  roleEdit: 'Roles not allowed to see this column in Edit form. Selected via searchable multi-select dropdown.',
  roleReadonly: 'Roles for which this field will be read-only in both Add and Edit modals. Selected via searchable multi-select dropdown.',
  roleReadonlyAdd: 'Roles for which this field will be read-only in Add form modal. Selected via searchable multi-select dropdown.',
  roleReadonlyEdit: 'Roles for which this field will be read-only in Edit form modal. Selected via searchable multi-select dropdown.',
  colType: 'Input type when rendering this field in form / grid',
  formula: 'Arithmetic expression to compute column value from other columns in each row (e.g. (balance + coupon_amount) / grid_balance)',
  rowspan: 'Enable row spanning for this column across consecutive identical values or group keys',
  rowFormula: 'Row calculation formula (e.g. sum(duration_dg), avg(val), min(val), max(val), count(val))',
  groupBy: 'Columns used to group rows for row calculations and row spanning (e.g. sensor_name)',
  valign: 'Vertical alignment of row-spanned text (top, middle, bottom)',
  repeatOnEveryPage: 'Whether row-spanned text repeats on every page in PDF export (true for main labels, false for totals)',
  regex: 'Regular expression validation pattern',
  regexMessage: 'Validation message shown when regex fails',
  optionsMode: 'Source for dropdown options (none, static list, or API)',
  optionsApi: 'API endpoint to fetch dropdown options dynamically',
  optionsPayload: 'Static JSON payload merged and sent when fetching dropdown choices',
  optionsKey: 'Property name in option object used as value',
  optionsValue: 'Property name in option object used as label',
  mapping: 'Transform raw values to human-readable labels via URL or JSON map',
  mappingPayload: 'Static JSON payload sent with Mapping URL request',
  click: 'Action when clicking a cell in this column',
  clickRedirect: 'Target URL to navigate to when cell is clicked',
  clickPopup: 'API URL to load content into popup modal when clicked',
  clickPayload: 'Static JSON payload sent with click redirect, popup, or form action',
  addRouteUrl: 'Application route URL to navigate to when clicking Add',
  editRouteUrl: 'Application route URL to navigate to when clicking Edit',
  minDate: 'Minimum selectable date (e.g. today or YYYY-MM-DD)',
  maxDate: 'Maximum selectable date (e.g. today or YYYY-MM-DD)',
  monthYear: 'Only allow selecting Month and Year'
};

interface ConfigNode {
  _id: string;
  name: string;
  type: 'M' | 'S' | 'I';
  parent_id?: string | null;
  sub_module: 'Y' | 'N';
}

type ComponentType = 'form' | 'table' | 'modalform';
type BuildBasis = 'api' | 'new';

@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [CommonModule, FormsModule, KnowledgeBaseComponent, NgbModule, DragDropModule],
  templateUrl: './component-builder.html',
  styleUrl: './component-builder.scss'
})
export class ComponentBuilder implements OnInit {
  private http = inject(HttpClient);
  private roleService = inject(RoleService);
  private apiCallService = inject(ApiCallsService);
  private toast = inject(ToastService)
  tableConfigId = '';
  tableParentId = ''; // NEW — persists across JSON edit round-trips
  headerJsonText = '';
  headerJsonError = '';
  headerConvertWarnings: string[] = [];
  // ---- Hierarchy state ----
  modules: ConfigNode[] = [];
  submodules: ConfigNode[] = [];
  innermodules: ConfigNode[] = [];

  selectedModuleId = '';
  selectedSubModuleId = '';
  selectedInnerModuleId = '';

  loadingHierarchy = false;
  hierarchyError = '';

  // ---- Component type / basis ----
  componentType: ComponentType | '' = '';
  basis: BuildBasis | '' = '';

  // ---- Table state ----
  tableMeta: TableMeta = this.blankTableMeta();
  tableRows: TableColumnRow[] = [];
  originalColumns: { key: string; title: string }[] = [];
  originalTableRows: TableColumnRow[] = [];
  removedColumns: { row: TableColumnRow; index: number }[] = [];
  removedColumnsTracker: { key: string; title: string }[] = [];
  roles: RoleOption[] = [];
  loadingRoles = false;
  rolesError = '';

  tableConfigLoading = false;
  tableConfigWarning = '';
  isExistingTableConfig = false;

  tableViewMode: 'form' | 'json' | 'convert' | 'oldImport' = 'form';
  tableJsonError = '';
  tableJsonText = '';

  constructor() { }
  oldFormatJsonText = '';
  oldFormatJsonError = '';
  oldFormatConvertWarnings: string[] = [];
  readonly fieldInfo = FIELD_INFO;

  readonly columnTypes: { value: ColumnType; label: string }[] = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'dropdownUrl', label: 'Dropdown (API)' },
    { value: 'dropdownList', label: 'Dropdown (List)' },
    { value: 'bitcase', label: 'Bit Case Checkbox' }
  ];

  readonly conversionTypes = [
    { value: 'decimal', label: 'Decimal (2)' },
    { value: 'octal', label: 'Octal (8)' },
    { value: 'hexadecimal', label: 'Hexadecimal (16)' }
  ];

  readonly bitCountOptions: { value: number; label: string }[] = [
    { value: 1, label: '1 Checkbox (1 Bit)' },
    { value: 2, label: '2 Checkboxes (2 Bits)' },
    { value: 3, label: '3 Checkboxes (3 Bits)' },
    { value: 4, label: '4 Checkboxes (4 Bits)' },
    { value: 8, label: '8 Checkboxes (8 Bits)' },
    { value: 12, label: '12 Checkboxes (12 Bits)' },
    { value: 16, label: '16 Checkboxes (16 Bits)' },
    { value: 24, label: '24 Checkboxes (24 Bits)' },
    { value: 32, label: '32 Checkboxes (32 Bits)' }
  ];
  tableJsonSnapshot = ''; // last text that was actually applied to the form model

  get isTableJsonDirty(): boolean {
    return this.tableViewMode === 'json' && this.tableJsonText !== this.tableJsonSnapshot;
  }
  /** Boolean flag keys rendered in the "Behavior" row of each column card */
  readonly columnBoolFlags: ColumnBoolFlag[] = ['show', 'add', 'edit', 'view', 'readonly', 'readonly_add', 'readonly_edit', 'required', 'display', 'download', 'total', 'ignore_value'];
  // ---- Form / ModalForm state (unchanged) ----
  formFields: FieldConfig[] = [];
  modalForm: ModalFormConfig = { modalTitle: '', modalSize: 'md', fields: [] };

  ngOnInit(): void {
    this.loadModules();
  }

  // ---- Hierarchy getters ----
  get selectedModule(): ConfigNode | undefined {
    return this.modules.find(m => m._id === this.selectedModuleId);
  }

  get selectedSubModule(): ConfigNode | undefined {
    return this.submodules.find(s => s._id === this.selectedSubModuleId);
  }

  get selectedInnerModule(): ConfigNode | undefined {
    return this.innermodules.find(i => i._id === this.selectedInnerModuleId);
  }

  get currentHierarchyId(): string {
    return this.selectedInnerModuleId || this.selectedSubModuleId || this.selectedModuleId || '';
  }

  get currentHierarchyNode(): { id: string; name: string } | null {
    if (this.selectedInnerModule) return { id: this.selectedInnerModule._id, name: this.selectedInnerModule.name };
    if (this.selectedSubModule) return { id: this.selectedSubModule._id, name: this.selectedSubModule.name };
    if (this.selectedModule) return { id: this.selectedModule._id, name: this.selectedModule.name };
    return null;
  }

  get hierarchyResolved(): boolean {
    if (!this.selectedModuleId) return false;
    if (this.selectedModule?.sub_module === 'Y' && !this.selectedSubModuleId) return false;
    if (this.selectedSubModule?.sub_module === 'Y' && !this.selectedInnerModuleId) return false;
    return true;
  }

  get canSave(): boolean {
    return this.hierarchyResolved && !!this.componentType && !!this.basis;
  }

  // ---- Configuration API (hierarchy) ----
  private loadConfig(type: 'M' | 'S' | 'I', parentId?: string): Observable<ConfigNode[]> {
    let params = new HttpParams().set('type', type);
    if (parentId) params = params.set('parent_id', parentId);

    return this.http.get<any>(environment.apiURL + '/Configuration', { params }).pipe(
      map(res => {
        if (res?.Status === 'Success') {
          return (res?.Data ?? []) as ConfigNode[];
        } else {
          this.hierarchyError = res?.message || 'Failed to load configuration list.';
          return [];
        }
      }),
      catchError(() => {
        this.hierarchyError = 'Failed to load configuration list.';
        return of([]);
      })
    );
  }

  loadModules(): void {
    this.loadingHierarchy = true;
    this.hierarchyError = '';
    this.loadConfig('M').subscribe(list => {
      this.modules = list;
      this.loadingHierarchy = false;
    });
  }

  onModuleChange(): void {
    this.submodules = [];
    this.innermodules = [];
    this.selectedSubModuleId = '';
    this.selectedInnerModuleId = '';
    this.resetComponentBuild();
    this.hierarchyError = '';

    if (this.selectedModule?.sub_module === 'Y') {
      this.loadingHierarchy = true;
      this.loadConfig('S', this.selectedModuleId).subscribe(list => {
        this.submodules = list;
        this.loadingHierarchy = false;
      });
    }
  }

  onSubModuleChange(): void {
    this.innermodules = [];
    this.selectedInnerModuleId = '';
    this.resetComponentBuild();
    this.hierarchyError = '';

    if (this.selectedSubModule?.sub_module === 'Y') {
      this.loadingHierarchy = true;
      this.loadConfig('I', this.selectedSubModuleId).subscribe(list => {
        this.innermodules = list;
        this.loadingHierarchy = false;
      });
    }
  }

  onInnerModuleChange(): void {
    this.resetComponentBuild();
  }

  // ---- Component type / basis ----
  onComponentTypeChange(): void {
    this.basis = '';
    this.resetTableBuild();
    this.formFields = [];
    this.modalForm = { modalTitle: '', modalSize: 'md', fields: [] };

    if (this.componentType === 'form') this.addFieldTo(this.formFields);
    if (this.componentType === 'modalform') this.addFieldTo(this.modalForm.fields);
    if (this.componentType === 'table' && this.roles.length === 0) this.loadRoles();
  }

  onBasisChange(): void {
    this.resetTableBuild();

    if (this.basis === 'new' && this.componentType === 'table') {
      this.addTableColumn();
    }

    if (this.basis === 'api' && this.componentType === 'table') {
      this.fetchTableConfig();
    }
  }

  private resetComponentBuild(): void {
    this.componentType = '';
    this.basis = '';
    this.resetTableBuild();
    this.formFields = [];
    this.modalForm = { modalTitle: '', modalSize: 'md', fields: [] };
  }

  private resetTableBuild(): void {
    this.tableConfigId = '';
    this.tableParentId = ''; // NEW
    this.tableMeta = this.blankTableMeta();
    this.tableRows = [];
    this.originalColumns = [];
    this.originalTableRows = [];
    this.removedColumns = [];
    this.removedColumnsTracker = [];
    this.tableConfigWarning = '';
    this.tableJsonText = '';
    this.tableJsonError = '';
    this.tableViewMode = 'form';
    this.isExistingTableConfig = false;
  }

  readonly filterFlagList: string[] = [
    'showSites',
    'SiteOnly',
    'showLocation',
    'FromToLocation',
    'SingleLocationDropdown',
    'showDaterange',
    'showSingleDate',
    'Month_Year',
    'Month_Range',
    'Date_Time',
    'Date_Flat',
    'AllSelection',
    'showsubtype',
    'showappliances',
    'showBillNo',
    'Hours',
    'search'
  ];

  private blankTableMeta(): TableMeta {
    return {
      title: '', listTitle: '', dataUrl: '', dataPayload: null, dataPayloadStr: '',
      addUrl: '', addPayload: null, addPayloadStr: '',
      editUrl: '', editPayload: null, editPayloadStr: '',
      add_routeurl: '', edit_routeurl: '',   // NEW
      add: false, edit: false,
      csv: false, pdf: false, load_after_filter: false, pagination: true,
      enableCheckbox: false, is_reprint: false, tableActionType: 'printReceipt', tableActionLabel: 'Print', tableActionBadge: 'Select receipts to print',
      filterEnabled: false,
      filterFlags: {
        showSites: false,
        SiteOnly: false,
        showLocation: false,
        FromToLocation: false,
        SingleLocationDropdown: false,
        showDaterange: false,
        showSingleDate: false,
        Month_Year: false,
        Month_Range: false,
        Date_Time: false,
        Date_Flat: false,
        AllSelection: false,
        showsubtype: false,
        showappliances: false,
        showBillNo: false,
        Hours: false,
        search: false
      },
      filterConfig: []
    };
  }
  // ---- Boolean flag get/set — accepts plain string so *ngFor arrays work directly ----
  getBoolFlag(row: TableColumnRow, flag: string): boolean {
    if (flag === 'readonly') {
      return !!(row.payload.readonly || (row.payload.readonly_add && row.payload.readonly_edit));
    }
    if (flag === 'readonly_add') {
      return !!(row.payload.readonly_add || row.payload.readonly);
    }
    if (flag === 'readonly_edit') {
      return !!(row.payload.readonly_edit || row.payload.readonly);
    }
    return !!(row.payload as any)[flag];
  }

  setBoolFlag(row: TableColumnRow, flag: string, value: boolean): void {
    if (flag === 'readonly') {
      row.payload.readonly = value;
      row.payload.readonly_add = value;
      row.payload.readonly_edit = value;
    } else if (flag === 'readonly_add') {
      row.payload.readonly_add = value;
      if (!value && row.payload.readonly) {
        row.payload.readonly = false;
      } else if (value && row.payload.readonly_edit) {
        row.payload.readonly = true;
      }
    } else if (flag === 'readonly_edit') {
      row.payload.readonly_edit = value;
      if (!value && row.payload.readonly) {
        row.payload.readonly = false;
      } else if (value && row.payload.readonly_add) {
        row.payload.readonly = true;
      }
    } else {
      (row.payload as any)[flag] = value;
    }
  }

  formatFlagLabel(flag: string): string {
    switch (flag) {
      case 'readonly': return 'Readonly';
      case 'readonly_add': return 'Readonly (Add)';
      case 'readonly_edit': return 'Readonly (Edit)';
      case 'total': return 'Total (Export)';
      case 'ignore_value': return 'Ignore Value';
      default: return flag;
    }
  }

  // ---- Payload Helpers ----
  parsePayload(strOrObj: any): any {
    if (strOrObj === undefined || strOrObj === null) return undefined;
    if (typeof strOrObj === 'object') return strOrObj;
    const trimmed = String(strOrObj).trim();
    if (!trimmed) return undefined;
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  formatPayloadStr(payload: any): string {
    if (payload === undefined || payload === null) return '';
    if (typeof payload === 'object') {
      return JSON.stringify(payload, null, 2);
    }
    return String(payload);
  }

  toggleRowKeyForClick(clk: any, key: string): void {
    if (!clk) return;
    let keys = (clk.rowKeysStr || '').split(',').map((s: string) => s.trim()).filter(Boolean);
    if (keys.includes(key)) {
      keys = keys.filter((k: string) => k !== key);
    } else {
      keys.push(key);
    }
    clk.rowKeysStr = keys.join(', ');
    clk.rowKeys = keys;
  }

  isRowKeySelected(clk: any, key: string): boolean {
    if (!clk || !clk.rowKeysStr) return false;
    const keys = clk.rowKeysStr.split(',').map((s: string) => s.trim()).filter(Boolean);
    return keys.includes(key);
  }

  // ---- Roles ----
  roleSearchTerm: Record<string, string> = {};

  loadRoles(): void {
    this.loadingRoles = true;
    this.rolesError = '';

    this.roleService
      .get()
      .pipe(finalize(() => (this.loadingRoles = false)))
      .subscribe({
        next: (res) => {
          if (res?.Status === 'Success') {
            const roles = res?.Data ?? res ?? [];

            this.roles = roles.map((role: any) => ({
              ...role,
              type:
                role.login_mode && role.login_mode.trim()
                  ? `${role.type}-${role.login_mode.toUpperCase()}`
                  : role.type
            }));
          } else {
            this.roles = [];
            this.rolesError = res?.message || 'Failed to load roles.';
          }
        },
        error: () => {
          this.roles = [];
          this.rolesError = 'Failed to load roles.';
        }
      });
  }

  getFilteredRoles(search = ''): RoleOption[] {
    if (!search || !search.trim()) return this.roles;
    const term = search.trim().toLowerCase();
    return this.roles.filter(r =>
      (r.name && r.name.toLowerCase().includes(term)) ||
      (r.type && r.type.toLowerCase().includes(term)) ||
      (r.login_mode && r.login_mode.toLowerCase().includes(term))
    );
  }

  getSelectedRoleLabel(selectedList: string[] = [], placeholder = 'Select roles...'): string {
    if (!selectedList || selectedList.length === 0) return placeholder;
    if (selectedList.length === 1) {
      const role = this.roles.find(r => r.type === selectedList[0]);
      return role ? (role.name || role.type) : selectedList[0];
    }
    if (selectedList.length === 2) {
      const r1 = this.roles.find(r => r.type === selectedList[0]);
      const r2 = this.roles.find(r => r.type === selectedList[1]);
      const n1 = r1 ? (r1.name || r1.type) : selectedList[0];
      const n2 = r2 ? (r2.name || r2.type) : selectedList[1];
      return `${n1}, ${n2}`;
    }
    return `${selectedList.length} roles selected`;
  }

  isRoleInList(list: string[] = [], roleType: string): boolean {
    return Array.isArray(list) && list.includes(roleType);
  }

  toggleRoleInList(holder: any, key: string, roleType: string): void {
    if (!holder[key] || !Array.isArray(holder[key])) {
      holder[key] = [];
    }
    if (holder[key].includes(roleType)) {
      holder[key] = holder[key].filter((r: string) => r !== roleType);
    } else {
      holder[key] = [...holder[key], roleType];
    }
  }

  selectAllRolesInList(holder: any, key: string, searchKey = ''): void {
    const matching = this.getFilteredRoles(this.roleSearchTerm[searchKey] || '');
    const currentSet = new Set(holder[key] || []);
    matching.forEach(r => currentSet.add(r.type));
    holder[key] = Array.from(currentSet);
  }

  deselectAllRolesInList(holder: any, key: string, searchKey = ''): void {
    const searchTerm = this.roleSearchTerm[searchKey] || '';
    if (!searchTerm.trim()) {
      holder[key] = [];
    } else {
      const matchingTypes = new Set(this.getFilteredRoles(searchTerm).map(r => r.type));
      holder[key] = (holder[key] || []).filter((r: string) => !matchingTypes.has(r));
    }
  }

  removeRoleFromList(holder: any, key: string, roleType: string): void {
    if (Array.isArray(holder[key])) {
      holder[key] = holder[key].filter((r: string) => r !== roleType);
    }
  }

  toggleRoleInTitleGroup(row: TableColumnRow, group: TitleOverrideGroup, roleType: string): void {
    if (group.roles.includes(roleType)) {
      group.roles = group.roles.filter(r => r !== roleType);
    } else {
      for (const g of row.titleOverrideGroups) {
        if (g !== group) g.roles = g.roles.filter(r => r !== roleType);
      }
      group.roles = [...group.roles, roleType];
    }
    this.syncTitleOverridesFromGroups(row);
  }

  getRoleNameByType(roleType: string): string {
    const r = this.roles.find(x => x.type === roleType);
    return r ? (r.name || r.type) : roleType;
  }

  isRoleSelected(row: TableColumnRow, roleId: string, field: 'role' | 'role_add' | 'role_edit' | 'role_readonly' | 'role_readonly_add' | 'role_readonly_edit' = 'role'): boolean {
    return row.payload[field]?.includes(roleId) ?? false;
  }

  toggleRoleChip(row: TableColumnRow, roleId: string, field: 'role' | 'role_add' | 'role_edit' | 'role_readonly' | 'role_readonly_add' | 'role_readonly_edit' = 'role'): void {
    if (!row.payload[field]) row.payload[field] = [];
    row.payload[field] = row.payload[field].includes(roleId)
      ? row.payload[field].filter(r => r !== roleId)
      : [...row.payload[field], roleId];
  }

  // ---- Filter fields (table-level) ----
  addTableFilterConfig(): void {
    this.tableMeta.filterConfig.push({
      label: '',
      apiKey: '',
      type: 'text',
      apiUrl: '',
      labelKey: '',
      valueKey: '',
      placeholder: '',
      col: 'col-12 col-sm-6 col-md-4 col-lg-3',
      required: false,
      StaticPayload: null,
      staticPayloadStr: '',
      options: [],
      value: ''
    });
  }

  removeTableFilterConfig(i: number): void {
    this.tableMeta.filterConfig.splice(i, 1);
  }

  addTableFilterOption(field: any): void {
    if (!field.options) field.options = [];
    field.options.push({ label: '', value: '' });
  }

  removeTableFilterOption(field: any, index: number): void {
    field.options.splice(index, 1);
  }

  // ---- Column CRUD ----
  addTableColumn(): void {
    this.tableRows.push(this.blankColumn());
  }

  removeTableColumn(i: number): void {
    const row = this.tableRows[i];
    if (row) {
      this.removedColumns.push({ row, index: i });
      if (row.payload) {
        const colKey = (row.payload.key || '').trim();
        const colTitle = (row.payload.title || '').trim();
        if (colKey || colTitle) {
          this.removedColumnsTracker.push({
            key: colKey,
            title: colTitle || colKey || `Column ${i + 1}`
          });
        }
      }
    }
    this.tableRows.splice(i, 1);
  }

  onColumnDrop(event: CdkDragDrop<TableColumnRow[]>): void {
    moveItemInArray(this.tableRows, event.previousIndex, event.currentIndex);
  }

  moveColumnUp(index: number, event?: Event): void {
    if (event) event.stopPropagation();
    if (index > 0) {
      moveItemInArray(this.tableRows, index, index - 1);
    }
  }

  moveColumnDown(index: number, event?: Event): void {
    if (event) event.stopPropagation();
    if (index < this.tableRows.length - 1) {
      moveItemInArray(this.tableRows, index, index + 1);
    }
  }

  onFormFieldDrop(fields: FieldConfig[], event: CdkDragDrop<FieldConfig[]>): void {
    moveItemInArray(fields, event.previousIndex, event.currentIndex);
  }

  onTableFilterDrop(event: CdkDragDrop<any[]>): void {
    if (this.tableMeta?.filterConfig) {
      moveItemInArray(this.tableMeta.filterConfig, event.previousIndex, event.currentIndex);
    }
  }

  toggleColumnExpanded(row: TableColumnRow): void {
    row.expanded = !row.expanded;
  }

  copyColumnKey(key: string | undefined, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!key || !key.trim()) {
      this.toast.showToast('No key to copy', 'colored', 2500, 'warning');
      return;
    }
    const cleanKey = key.trim();
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(cleanKey).then(() => {
        this.toast.showToast(`Key "${cleanKey}" copied`, 'colored', 3000, 'success');
      }).catch(() => {
        this.fallbackCopyText(cleanKey);
      });
    } else {
      this.fallbackCopyText(cleanKey);
    }
  }

  private fallbackCopyText(text: string): void {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.toast.showToast(`Key "${text}" copied`, 'colored', 3000, 'success');
    } catch {
      this.toast.showToast(`Failed to copy key`, 'colored', 3000, 'danger');
    }
  }

  getExistingKeysList(): string[] {
    return Array.from(new Set((this.tableRows || []).map(r => r.payload?.key?.trim()).filter((k): k is string => !!k)));
  }

  insertKeyIntoFormula(row: TableColumnRow, token: string): void {
    if (!row.payload) return;
    const current = (row.payload.formula || '').trim();
    const isOperator = ['+', '-', '*', '/', '(', ')', '%'].includes(token);
    if (!current) {
      row.payload.formula = token;
    } else {
      const endsWithOperator = /[\+\-\*\/\(\%\^]\s*$/.test(current);
      if (isOperator || endsWithOperator || token === '(' || token === ')') {
        row.payload.formula = `${current} ${token}`.trim();
      } else {
        row.payload.formula = `${current} + ${token}`.trim();
      }
    }
  }

  private readonly mathKeywords = new Set([
    'math', 'min', 'max', 'round', 'floor', 'ceil', 'abs', 'sqrt', 'pow', 'log',
    'exp', 'sin', 'cos', 'tan', 'true', 'false', 'null', 'undefined', 'nan',
    'infinity', 'sum', 'avg', 'count', 'if', 'then', 'else', 'val', 'value', 'row'
  ]);

  getFormulaKeyErrors(formula: string | undefined): { word: string; suggestion?: string }[] {
    if (!formula || !formula.trim()) return [];
    const existingKeys = this.getExistingKeysList();
    const existingKeysSet = new Set(existingKeys);

    const matches = formula.match(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g) || [];
    const errors: { word: string; suggestion?: string }[] = [];
    const seen = new Set<string>();

    matches.forEach(word => {
      const wLower = word.toLowerCase();
      if (this.mathKeywords.has(wLower)) return;
      if (!existingKeysSet.has(word) && !seen.has(word)) {
        seen.add(word);
        const suggestion = this.findClosestKey(word, existingKeys);
        errors.push({ word, suggestion });
      }
    });
    return errors;
  }

  hasFormulaKeyErrors(formula: string | undefined): boolean {
    return this.getFormulaKeyErrors(formula).length > 0;
  }

  private findClosestKey(word: string, keys: string[]): string | undefined {
    if (!keys.length) return undefined;
    const wLower = word.toLowerCase();
    let bestMatch: string | undefined = undefined;
    let minDistance = 999;

    for (const k of keys) {
      const kLower = k.toLowerCase();
      if (kLower === wLower) return k;
      if (kLower.includes(wLower) || wLower.includes(kLower)) {
        return k;
      }
      const dist = this.levenshteinDistance(wLower, kLower);
      if (dist < minDistance && dist <= Math.max(3, Math.floor(word.length / 2))) {
        minDistance = dist;
        bestMatch = k;
      }
    }
    return bestMatch;
  }

  private levenshteinDistance(a: string, b: string): number {
    const matrix: number[][] = [];
    for (let i = 0; i <= b.length; i++) matrix[i] = [i];
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[b.length][a.length];
  }

  onColumnTypeChange(row: TableColumnRow): void {
    if (row.payload.type === 'dropdownUrl') {
      row.optionsMode = 'api';
      row.payload.options = { url: '', key: 'id', value: 'name', list: [], extraOption: null };
    } else if (row.payload.type === 'dropdownList') {
      row.optionsMode = 'static';
      row.payload.options = { url: '', key: 'id', value: 'name', list: [{ id: '', name: '' }] };
    } else if (row.payload.type === 'bitcase') {
      row.optionsMode = 'none';
      row.payload.options = null;
      if (!row.payload.conversionType) row.payload.conversionType = 'decimal';
      if (!row.payload.bitCount) row.payload.bitCount = 12;
    } else {
      row.optionsMode = 'none';
      row.payload.options = null;
    }
  }

  toggleExtraOption(opts: OptionsConfig): void {
    if (opts.extraOption) {
      opts.extraOption = null;
    } else {
      opts.extraOption = { id: 'ALL', name: 'All Units' };
    }
  }

  toggleFieldExtraOption(field: FieldConfig): void {
    if (field.extraOption) {
      field.extraOption = null;
    } else {
      field.extraOption = { id: 'ALL', name: 'All Units' };
    }
  }

  addStaticOption(row: TableColumnRow): void {
    row.payload.options?.list.push({ id: '', name: '' });
  }

  removeStaticOption(row: TableColumnRow, index: number): void {
    row.payload.options?.list.splice(index, 1);
  }

  onClickToggle(row: TableColumnRow, enabled: boolean): void {
    row.payload.click = enabled ? { type: 'redirect', redirectUrl: '' } : null;
  }

  onClickTypeChange(row: TableColumnRow, type: ClickType): void {
    if (type === 'redirect') {
      row.payload.click = { type: 'redirect', redirectUrl: row.payload.click?.redirectUrl || '' };
    } else if (type === 'popup') {
      row.payload.click = {
        type: 'popup',
        popup: row.payload.click?.popup || { title: '', size: 'md', contentUrl: '' }
      };
    } else if (type === 'openForm') {
      row.payload.click = {
        type: 'openForm',
        apiKey: row.payload.click?.apiKey || '',
        fields: row.payload.click?.fields || []
      };
    }
  }

  getColumnFormFields(clk: ClickConfig): FieldConfig[] {
    if (!clk.fields) {
      clk.fields = [];
    }
    return clk.fields;
  }

  toggleFieldExpanded(field: FieldConfig): void {
    field.expanded = !field.expanded;
  }

  onFormFieldTypeChange(field: FieldConfig): void {
    if (field.type === 'dropdownUrl') {
      field.optionsMode = 'api';
      field.dropdown = true;
      field.apiUrl = field.apiUrl || '';
      field.optionKey = field.optionKey || 'id';
      field.option_mapping = field.option_mapping || { id: 'id', name: 'name' };
      if (!field.extraOption) field.extraOption = null;
    } else if (field.type === 'dropdownList') {
      field.optionsMode = 'static';
      field.dropdown = true;
      field.options = (field.options && field.options.length) ? field.options : [{ id: '', name: '' }];
      field.optionKey = 'id';
      field.option_mapping = { id: 'id', name: 'name' };
      field.extraOption = null;
    } else if (field.type === 'bitcase') {
      field.optionsMode = 'none';
      field.dropdown = false;
      field.apiUrl = '';
      field.extraOption = null;
      if (!field.conversionType) field.conversionType = 'decimal';
      if (!field.bitCount) field.bitCount = 12;
    } else {
      field.optionsMode = 'none';
      field.dropdown = false;
      field.apiUrl = '';
      field.extraOption = null;
    }
  }

  addStaticOptionToField(field: FieldConfig): void {
    if (!field.options) field.options = [];
    field.options.push({ id: '', name: '' });
  }

  removeStaticOptionFromField(field: FieldConfig, index: number): void {
    field.options?.splice(index, 1);
  }

  onMappingToggle(row: TableColumnRow, enabled: boolean): void {
    if (enabled) {
      row.payload.mapping = { mappingUrl: '', mappingKey: '', mappingValue: '', json: {} };
      row.mappingEntries = row.mappingEntries?.length ? row.mappingEntries : [{ key: '', value: '' }];
    } else {
      row.payload.mapping = null;
      row.mappingEntries = [];
    }
  }
  addMappingEntry(row: TableColumnRow): void {
    row.mappingEntries.push({ key: '', value: '' });
    this.syncMappingJsonFromEntries(row);
  }

  removeMappingEntry(row: TableColumnRow, index: number): void {
    row.mappingEntries.splice(index, 1);
    this.syncMappingJsonFromEntries(row);
  }

  onMappingEntryChange(row: TableColumnRow): void {
    this.syncMappingJsonFromEntries(row);
  }

  private syncMappingJsonFromEntries(row: TableColumnRow): void {
    if (!row.payload.mapping) return;
    const obj: Record<string, string> = {};
    for (const entry of row.mappingEntries) {
      if (entry.key.trim()) obj[entry.key] = entry.value;
    }
    row.payload.mapping.json = obj;
  }
  private parseOldFormatJson(text: string): Record<string, any> | null {
    if (!text.trim()) {
      this.oldFormatJsonError = 'JSON is empty.';
      return null;
    }

    let raw: any = null;

    // Fast path: it's genuinely valid JSON.
    try {
      raw = JSON.parse(text);
    } catch {
      // Fall back: treat it as a JS object literal pulled straight out of an
      // old .js/.php grid config — unquoted keys, single quotes, trailing
      // commas, and PHP template tags all need handling that JSON.parse can't do.
      const { code, warnings } = this.sanitizePhpTemplateJs(text);
      // Warnings reset by the caller (convertAndMergeOldFormat) before this runs.
      this.oldFormatConvertWarnings.push(...warnings);

      try {
        raw = this.tryEvalJsObjectWithBraceRepair(code);
      } catch (e: any) {
        this.oldFormatJsonError = `Could not parse as JSON or as a JS object literal: ${e.message}`;
        return null;
      }
    }

    const fields = raw?.fields && typeof raw.fields === 'object' ? raw.fields : raw;
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
      this.oldFormatJsonError = 'Expected an object of { key: { title, width, options, ... } } entries (or an object containing a "fields" object).';
      return null;
    }
    return fields;
  }

  /**
   * Neutralizes PHP template tags so the remaining text is valid JS:
   * - if/elseif/else/closing-brace control tags around alternate `options: {...}`
   *   blocks are removed but the JS between them is kept. Duplicate object keys
   *   are legal in JS (last one wins), which naturally lands on the `else`
   *   branch — the sane default for a client-side import.
   * - `<?php print $var; ?>` used as a value (quoted or bare) becomes `null`,
   *   since there's no server-rendered data to fall back on client-side.
   */
  private sanitizePhpTemplateJs(text: string): { code: string; warnings: string[] } {
    const warnings: string[] = [];
    let code = text;

    code = code.replace(/<\?php\s*if\s*\([^)]*\)\s*\{\s*\?>/gi, '');
    code = code.replace(/<\?php\s*\}\s*elseif\s*\([^)]*\)\s*\{\s*\?>/gi, '');
    code = code.replace(/<\?php\s*\}\s*else\s*\{\s*\?>/gi, '');
    code = code.replace(/<\?php\s*\}\s*\?>/gi, '');

    // Was [^']*? — broke on PHP values containing their own quotes, e.g.
    // '<?php print $_SESSION['site_id'];?>'. Match non-greedily up to the
    // FIRST "?>'" instead of excluding quotes altogether.
    code = code.replace(/'<\?php[\s\S]*?\?>'/gi, () => {
      warnings.push('A PHP-rendered value (quoted, e.g. a session var or date()) was replaced with null — fill it in manually.');
      return 'null';
    });
    code = code.replace(/<\?php\s*print\s+[^;]+;\s*\?>/gi, () => {
      warnings.push('A PHP-rendered options/value list from the server was replaced with null — fill it in manually.');
      return 'null';
    });

    if (/<\?php/i.test(code)) {
      code = code.replace(/<\?php[\s\S]*?\?>/gi, () => {
        warnings.push('An unrecognized PHP block was stripped — review that field manually.');
        return 'null';
      });
    }

    return { code, warnings };
  }

  /** Evaluates a JS object literal, repairing common copy-paste artifacts:
   *  a dangling trailing comma (fragment pulled out of a larger object, e.g.
   *  "fields: { ... },") and/or one stray extra closing brace. */
  private tryEvalJsObjectWithBraceRepair(code: string): any {
    const stripTrailingComma = (s: string) => s.replace(/[,;]\s*$/, '');

    const base = stripTrailingComma(code.trim());
    const attempts = [code, base];

    const withoutLastBrace = stripTrailingComma(base.replace(/\}\s*$/, '').trim());
    if (withoutLastBrace !== base) attempts.push(withoutLastBrace);

    let lastError: any;
    for (const attempt of attempts) {
      try {
        // eslint-disable-next-line no-new-func
        return new Function('"use strict"; return (' + attempt + ');')();
      } catch (e) {
        lastError = e;
      }
    }
    throw lastError;
  }
  /**
   * Imports the old grid "fields: {...}" config and merges it into the current
   * tableRows. Per spec: any key that already exists in the current structure
   * is left completely untouched (not overwritten, not removed) — only keys
   * that don't map to an existing column are added as new rows.
   */

  convertAndMergeOldFormat(): void {
    this.oldFormatJsonError = '';
    this.oldFormatConvertWarnings = [];   // parseOldFormatJson will push into this

    const fields = this.parseOldFormatJson(this.oldFormatJsonText);
    if (!fields) return;

    const existingKeys = new Set(this.tableRows.filter(r => !!r.payload.key).map(r => r.payload.key));

    for (const key of Object.keys(fields)) {
      if (existingKeys.has(key)) {
        this.oldFormatConvertWarnings.push(`"${key}" already exists — left unchanged.`);
        continue;
      }

      const field = fields[key] ?? {};
      const row = this.blankColumn(key, field.title ?? key);
      const isHidden = field.type === 'hidden' || field.visibility === 'hidden';

      row.payload.show = field.list === false ? false : !isHidden;
      row.payload.add = field.create === false ? false : !isHidden;
      row.payload.edit = field.edit === false || isHidden ? false : true;
      row.payload.view = true;
      row.payload.readonly = !!field.readonly;
      row.payload.download = row.payload.show;

      if (field.class) row.payload.class = field.class;
      if (field.inputClass) {
        row.payload.class = row.payload.class ? `${row.payload.class} ${field.inputClass}` : field.inputClass;
        this.oldFormatConvertWarnings.push(`"${key}" had inputClass "${field.inputClass}" — mapped to class, verify validation manually.`);
      }

      if (field.options && typeof field.options === 'object' && !Array.isArray(field.options)) {
        row.payload.type = 'dropdownList';
        row.optionsMode = 'static';
        row.payload.options = {
          url: '',
          key: 'id',
          value: 'name',
          list: Object.keys(field.options).map(optKey => ({ id: optKey, name: field.options[optKey] }))
        };
      } else if (field.type === 'date') {
        row.payload.type = 'date';
      } else {
        row.payload.type = 'text';
      }

      if (field.defaultValue !== undefined && field.defaultValue !== null) {
        this.oldFormatConvertWarnings.push(`"${key}" had a defaultValue ("${field.defaultValue}") — the new schema has no equivalent, review manually.`);
      }

      this.tableRows.push(row);
    }

    this.tableViewMode = 'form';
    this.toast.showToast('Old-format fields imported and merged successfully', 'colored', 5000, 'success');
  }
  private blankColumn(key = '', title = ''): TableColumnRow {
    return {
      payload: {
        key, title,
        titleOverrides: {},
        Order_By: '',
        show: true, add: false, edit: false, view: true, readonly: false, readonly_add: false, readonly_edit: false, required: false, display: true,
        class: '', regex: '', regexMessage: '', type: 'text', formula: '',
        click: null, options: null, mapping: null,
        download: false, role: [], role_add: [], role_edit: [],
        role_readonly: [], role_readonly_add: [], role_readonly_edit: [], status: 'Y',
        ignore_value: false,
        value: '',
        conversionType: 'decimal', bitCount: 12, bitLabels: [], bitLabelsStr: '',
        minDate: '', maxDate: '', monthYear: false
      },
      optionsMode: 'none',
      expanded: true,
      mappingEntries: [],
      titleOverrideEnabled: false,
      titleOverrideGroups: []
    };
  }
  getTitleOverride(row: TableColumnRow, loginType: string): string {
    return row.payload.titleOverrides?.[loginType] ?? '';
  }

  setTitleOverride(row: TableColumnRow, loginType: string, value: string): void {
    if (!row.payload.titleOverrides) row.payload.titleOverrides = {};
    if (value.trim()) {
      row.payload.titleOverrides[loginType] = value;
    } else {
      delete row.payload.titleOverrides[loginType]; // empty input = "use base title"
    }
  }

  // ---- Strip-defaults helper ----
  private stripDefaults(obj: Record<string, any>, defaults: Record<string, any>): Record<string, any> {
    const out: Record<string, any> = {};
    for (const k of Object.keys(obj)) {
      const val = obj[k];
      const def = defaults[k];
      if (def !== undefined && JSON.stringify(val) === JSON.stringify(def)) continue;
      out[k] = val;
    }
    return out;
  }
  private buildMappingEntriesFromJson(json: any): MappingEntry[] {
    let obj = json;
    if (typeof json === 'string' && json.trim()) {
      try { obj = JSON.parse(json); } catch { obj = null; }
    }
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return [{ key: '', value: '' }];
    }
    const entries = Object.keys(obj).map(k => ({ key: k, value: obj[k] }));
    return entries.length ? entries : [{ key: '', value: '' }];
  }
  private buildColumnPayload(row: TableColumnRow): any {
    const { key, _id, ...rest } = row.payload;
    const normalized: any = { ...rest };

    if (row.payload.Order_By !== undefined && row.payload.Order_By !== null && String(row.payload.Order_By).trim() !== '') {
      normalized.Order_By = isNaN(Number(row.payload.Order_By)) ? String(row.payload.Order_By).trim() : Number(row.payload.Order_By);
    } else {
      delete normalized.Order_By;
    }

    if (normalized.titleOverrides && Object.keys(normalized.titleOverrides).length === 0) {
      delete normalized.titleOverrides;
    }

    if (normalized.options) {
      normalized.options = { ...normalized.options };
      if (normalized.options.payloadStr && normalized.options.payloadStr.trim()) {
        normalized.options.payload = this.parsePayload(normalized.options.payloadStr);
      }
      delete (normalized.options as any).payloadStr;
      if (!normalized.options.payload || (typeof normalized.options.payload === 'object' && Object.keys(normalized.options.payload).length === 0)) {
        delete normalized.options.payload;
      }
      if (normalized.options.matchKey && normalized.options.matchKey.trim()) {
        normalized.options.matchKey = normalized.options.matchKey.trim();
      } else {
        delete normalized.options.matchKey;
      }
      if (normalized.options.extraOption) {
        const extraId = String(normalized.options.extraOption.id ?? '').trim();
        const extraName = String(normalized.options.extraOption.name ?? '').trim();
        if (extraId || extraName) {
          const finalExtra = {
            id: extraId || 'ALL',
            name: extraName || 'All Units'
          };
          normalized.options.extraOption = finalExtra;
          if (!normalized.mapping) {
            // No mapping exists yet — auto-create one with the extraOption as its only json entry
            normalized.mapping = {
              mappingUrl: normalized.options.url,
              mappingKey: normalized.options.key,
              mappingValue: normalized.options.value,
              json: { [finalExtra.id]: finalExtra.name }
            };
          }
          // If mapping already exists, leave mapping.json fully controlled by mappingEntries.
          // Don't force-inject the extraOption back in — that prevented users from removing it.
        } else {
          delete normalized.options.extraOption;
        }
      } else {
        delete normalized.options.extraOption;
      }
    }

    if (normalized.mapping) {
      normalized.mapping = { ...normalized.mapping };
      if (normalized.mapping.payloadStr && normalized.mapping.payloadStr.trim()) {
        normalized.mapping.payload = this.parsePayload(normalized.mapping.payloadStr);
      }
      delete (normalized.mapping as any).payloadStr;
      if (!normalized.mapping.payload || (typeof normalized.mapping.payload === 'object' && Object.keys(normalized.mapping.payload).length === 0)) {
        delete normalized.mapping.payload;
      }

      // json is already an object (built from entries) by the time we get here,
      // but stay defensive in case it's still a raw string (e.g. legacy data).
      if (typeof normalized.mapping.json === 'string' && normalized.mapping.json.trim()) {
        try {
          normalized.mapping.json = JSON.parse(normalized.mapping.json);
        } catch (err) {
          console.log(err);
        }
      }
      if (!normalized.mapping.json || (typeof normalized.mapping.json === 'object' && Object.keys(normalized.mapping.json).length === 0)) {
        delete normalized.mapping.json;
      }
    }

    if (normalized.click) {
      normalized.click = { ...normalized.click };
      if (normalized.click.payloadStr && normalized.click.payloadStr.trim()) {
        normalized.click.payload = this.parsePayload(normalized.click.payloadStr);
      }
      delete (normalized.click as any).payloadStr;

      if (normalized.click.popup) {
        normalized.click.popup = { ...normalized.click.popup };
        if (normalized.click.popup.payloadStr && normalized.click.popup.payloadStr.trim()) {
          normalized.click.popup.payload = this.parsePayload(normalized.click.popup.payloadStr);
        }
        delete (normalized.click.popup as any).payloadStr;
      }

      if (normalized.click.rowKeysStr) {
        normalized.click.rowKeys = normalized.click.rowKeysStr.split(',').map((s: string) => s.trim()).filter(Boolean);
      }
      delete (normalized.click as any).rowKeysStr;

      if (normalized.click.confirmAction) {
        normalized.click.confirmAction = true;
      } else {
        delete normalized.click.confirmAction;
      }

      if (normalized.click.warningMessage && normalized.click.warningMessage.trim()) {
        normalized.click.warningMessage = normalized.click.warningMessage.trim();
      } else {
        delete normalized.click.warningMessage;
      }

      if (normalized.click.type === 'openForm' && Array.isArray(normalized.click.fields)) {
        normalized.click.fields = this.serializeFields(normalized.click.fields);
      }
    }

    if (normalized.readonly || (normalized.readonly_add && normalized.readonly_edit)) {
      normalized.readonly = true;
      delete normalized.readonly_add;
      delete normalized.readonly_edit;
    } else {
      delete normalized.readonly;
      if (normalized.readonly_add) {
        normalized.readonly_add = true;
      } else {
        delete normalized.readonly_add;
      }
      if (normalized.readonly_edit) {
        normalized.readonly_edit = true;
      } else {
        delete normalized.readonly_edit;
      }
    }

    if (normalized.type === 'bitcase') {
      normalized.conversionType = row.payload.conversionType || 'decimal';
      normalized.bitCount = Number(row.payload.bitCount) || 12;
      if (row.payload.bitLabelsStr) {
        normalized.bitLabels = row.payload.bitLabelsStr.split(',').map((s: string) => s.trim()).filter(Boolean);
      } else if (Array.isArray(row.payload.bitLabels) && row.payload.bitLabels.length) {
        normalized.bitLabels = row.payload.bitLabels;
      }
      if (row.payload.minBase !== undefined && row.payload.minBase !== null && String(row.payload.minBase).trim() !== '') {
        normalized.minBase = isNaN(Number(row.payload.minBase)) ? row.payload.minBase : Number(row.payload.minBase);
      }
      if (row.payload.maxBase !== undefined && row.payload.maxBase !== null && String(row.payload.maxBase).trim() !== '') {
        normalized.maxBase = isNaN(Number(row.payload.maxBase)) ? row.payload.maxBase : Number(row.payload.maxBase);
      }
      if (row.payload.baseValue !== undefined && row.payload.baseValue !== null && String(row.payload.baseValue).trim() !== '') {
        normalized.baseValue = isNaN(Number(row.payload.baseValue)) ? row.payload.baseValue : Number(row.payload.baseValue);
      }
      normalized.bitDirection = row.payload.bitDirection || 'ltr';
      delete (normalized as any).bitLabelsStr;
    }

    if (normalized.type === 'date') {
      if (row.payload.minDate !== undefined && row.payload.minDate !== null && String(row.payload.minDate).trim() !== '') {
        normalized.minDate = String(row.payload.minDate).trim();
      } else {
        delete normalized.minDate;
      }
      if (row.payload.maxDate !== undefined && row.payload.maxDate !== null && String(row.payload.maxDate).trim() !== '') {
        normalized.maxDate = String(row.payload.maxDate).trim();
      } else {
        delete normalized.maxDate;
      }
      if (row.payload.monthYear) {
        normalized.monthYear = true;
      } else {
        delete normalized.monthYear;
      }
    }

    if (row.payload.ignore_value) {
      normalized.ignore_value = true;
    }
    if (row.payload.value) {
      normalized.value = row.payload.value;
    }

    const stripped = this.stripDefaults(normalized, COLUMN_DEFAULTS);
    if (_id) stripped['_id'] = _id;
    return stripped;
  }

  // ---- Serialize UI state -> backend document ----
  buildTablePayload(): any {
    const columns: Record<string, any> = {};
    let blankKeyCount = 0;
    for (const row of this.tableRows) {
      let key = (row.payload.key !== undefined && row.payload.key !== null) ? String(row.payload.key) : '';
      if (!key.trim()) {
        key = ' '.repeat(blankKeyCount + 1);
        blankKeyCount++;
      }
      columns[key] = this.buildColumnPayload(row);
    }

    const download = {
      csv: !!this.tableMeta.csv,
      pdf: !!this.tableMeta.pdf
    };

    const activeFlags: Record<string, boolean> = {};
    if (this.tableMeta.filterFlags) {
      Object.keys(this.tableMeta.filterFlags).forEach(k => {
        if (this.tableMeta.filterFlags[k]) {
          activeFlags[k] = true;
        }
      });
    }

    const filter: any = this.tableMeta.filterEnabled
      ? {
        enabled: true,
        ...activeFlags,
        filterConfig: (this.tableMeta.filterConfig || []).map(f => {
          const item: any = {
            label: f.label || f.title || '',
            type: f.type || 'text',
            apiKey: f.apiKey || f.key || '',
            placeholder: f.placeholder || '',
            col: f.col || 'col-12 col-sm-6 col-md-4 col-lg-3',
            value: f.value ?? ''
          };
          if (f.apiUrl || f.url) item.apiUrl = f.apiUrl || f.url;
          if (f.labelKey) item.labelKey = f.labelKey;
          if (f.valueKey) item.valueKey = f.valueKey;
          if (f.required) item.required = f.required;
          if (f.staticPayloadStr && f.staticPayloadStr.trim()) {
            try {
              item.StaticPayload = JSON.parse(f.staticPayloadStr);
            } catch {
              item.StaticPayload = f.staticPayloadStr;
            }
          } else if (f.StaticPayload && typeof f.StaticPayload === 'object' && Object.keys(f.StaticPayload).length) {
            item.StaticPayload = f.StaticPayload;
          }
          if (f.options && f.options.length) item.options = f.options;
          return item;
        })
      }
      : undefined;

    const list: any = { title: this.tableMeta.listTitle, dataUrl: this.tableMeta.dataUrl, columns };
    if (this.tableMeta.load_after_filter) {
      list.load_after_filter = true;
    }
    list.pagination = this.tableMeta.pagination !== false;
    if (this.tableMeta.enableCheckbox || this.tableMeta.is_reprint) {
      list.enableCheckbox = true;
      list.is_reprint = true;
      if (this.tableMeta.tableActionType) {
        list.tableAction = {
          type: this.tableMeta.tableActionType,
          label: this.tableMeta.tableActionLabel || 'Print',
          badge: this.tableMeta.tableActionBadge || 'Select receipts to print'
        };
      }
    }
    const dataPayload = this.parsePayload(this.tableMeta.dataPayloadStr || this.tableMeta.dataPayload);
    if (dataPayload) list.payload = dataPayload;

    list.add = this.tableMeta.add;
    list.edit = this.tableMeta.edit;
    if (this.tableMeta.addUrl) {
      list.addUrl = this.tableMeta.addUrl;
      const addPayload = this.parsePayload(this.tableMeta.addPayloadStr || this.tableMeta.addPayload);
      if (addPayload) list.addPayload = addPayload;
    }
    if (this.tableMeta.editUrl) {
      list.editUrl = this.tableMeta.editUrl;
      const editPayload = this.parsePayload(this.tableMeta.editPayloadStr || this.tableMeta.editPayload);
      if (editPayload) list.editPayload = editPayload;
    }
    if (this.tableMeta.add_routeurl) list.add_routeurl = this.tableMeta.add_routeurl;   // NEW
    if (this.tableMeta.edit_routeurl) list.edit_routeurl = this.tableMeta.edit_routeurl; // NEW

    const payload: any = {
      title: this.tableMeta.title,
      type: 'TABLE',
      list,
      download
    };
    if (this.tableMeta.filterEnabled && filter) {
      if (this.tableMeta.load_after_filter) {
        filter.load_after_filter = true;
      }
      payload.filter = filter;
    }

    const parentId = this.tableParentId || this.currentHierarchyId;
    if (parentId) payload.parent_id = parentId;

    if (this.isExistingTableConfig && this.tableConfigId) {
      payload.id = this.tableConfigId;
    }

    return payload;
  }
  // ---- Hydrate backend document -> UI state ----
  private hydrateFromConfig(cfg: any): void {
    // Accept either "id" or "_id" from the incoming doc, and NEVER wipe out
    // an id/parent_id we already have just because the pasted JSON omitted it.
    const incomingId = cfg.id ?? cfg._id;
    if (incomingId) {
      this.tableConfigId = incomingId;
    }

    if (cfg.parent_id) {
      this.tableParentId = cfg.parent_id;
    }

    const rawDataPayload = cfg.list?.payload ?? cfg.list?.dataPayload;
    const rawAddPayload = cfg.list?.addPayload ?? cfg.list?.add_payload;
    const rawEditPayload = cfg.list?.editPayload ?? cfg.list?.edit_payload;

    this.tableMeta = {
      title: cfg.title || '',
      listTitle: cfg.list?.title || '',
      dataUrl: cfg.list?.dataUrl || '',
      dataPayload: rawDataPayload || null,
      dataPayloadStr: this.formatPayloadStr(rawDataPayload),
      addUrl: cfg.list?.addUrl || '',
      addPayload: rawAddPayload || null,
      addPayloadStr: this.formatPayloadStr(rawAddPayload),
      editUrl: cfg.list?.editUrl || '',
      editPayload: rawEditPayload || null,
      editPayloadStr: this.formatPayloadStr(rawEditPayload),
      add_routeurl: cfg.list?.add_routeurl || '',   // NEW
      edit_routeurl: cfg.list?.edit_routeurl || '', // NEW

      add: !!cfg.list?.add,
      edit: !!cfg.list?.edit,
      csv: !!cfg.download?.csv,
      pdf: !!cfg.download?.pdf,
      load_after_filter: !!(
        cfg.list?.load_after_filter ??
        cfg.list?.loadAfterFilter ??
        cfg.filter?.load_after_filter ??
        cfg.filter?.loadAfterFilter ??
        cfg.load_after_filter ??
        cfg.loadAfterFilter ??
        false
      ),
      pagination: cfg.list?.pagination !== undefined
        ? !!cfg.list?.pagination
        : cfg.pagination !== undefined
          ? !!cfg.pagination
          : (cfg.list?.dataUrl === 'SitesList' ? false : true),
      enableCheckbox: !!(cfg.list?.enableCheckbox ?? cfg.list?.is_reprint ?? cfg.enableCheckbox ?? cfg.is_reprint ?? false),
      is_reprint: !!(cfg.list?.is_reprint ?? cfg.list?.enableCheckbox ?? cfg.is_reprint ?? cfg.enableCheckbox ?? false),
      tableActionType: cfg.list?.tableAction?.type || (cfg.list?.is_reprint || cfg.is_reprint ? 'printReceipt' : ''),
      tableActionLabel: cfg.list?.tableAction?.label || 'Print',
      tableActionBadge: cfg.list?.tableAction?.badge || 'Select receipts to print',
      filterEnabled: !!(cfg.filter && cfg.filter.enabled !== false),
      filterFlags: {
        showSites: !!(cfg.filter?.showSites ?? cfg.showSites ?? false),
        SiteOnly: !!(cfg.filter?.SiteOnly ?? cfg.SiteOnly ?? false),
        showLocation: !!(cfg.filter?.showLocation ?? cfg.showLocation ?? false),
        FromToLocation: !!(cfg.filter?.FromToLocation ?? cfg.FromToLocation ?? false),
        SingleLocationDropdown: !!(cfg.filter?.SingleLocationDropdown ?? cfg.SingleLocationDropdown ?? false),
        showDaterange: !!(cfg.filter?.showDaterange ?? cfg.showDaterange ?? false),
        showSingleDate: !!(cfg.filter?.showSingleDate ?? cfg.showSingleDate ?? false),
        Month_Year: !!(cfg.filter?.Month_Year ?? cfg.Month_Year ?? false),
        Month_Range: !!(cfg.filter?.Month_Range ?? cfg.Month_Range ?? false),
        Date_Time: !!(cfg.filter?.Date_Time ?? cfg.Date_Time ?? false),
        Date_Flat: !!(cfg.filter?.Date_Flat ?? cfg.Date_Flat ?? false),
        AllSelection: !!(cfg.filter?.AllSelection ?? cfg.AllSelection ?? false),
        showsubtype: !!(cfg.filter?.showsubtype ?? cfg.showsubtype ?? false),
        showappliances: !!(cfg.filter?.showappliances ?? cfg.showappliances ?? false),
        showBillNo: !!(cfg.filter?.showBillNo ?? cfg.showBillNo ?? false),
        Hours: !!(cfg.filter?.Hours ?? cfg.Hours ?? false),
        search: !!(cfg.filter?.search ?? cfg.search ?? false)
      },
      filterConfig: Array.isArray(cfg.filter?.filterConfig || cfg.filter?.fields || cfg.filterConfig)
        ? (cfg.filter?.filterConfig || cfg.filter?.fields || cfg.filterConfig).map((f: any) => ({
          label: f.label || f.title || '',
          type: f.type || 'text',
          apiKey: f.apiKey || f.key || '',
          apiUrl: f.apiUrl || f.url || '',
          labelKey: f.labelKey || f.optionLabel || '',
          valueKey: f.valueKey || f.optionKey || '',
          placeholder: f.placeholder || '',
          col: f.col || 'col-12 col-sm-6 col-md-4 col-lg-3',
          required: !!f.required,
          StaticPayload: f.StaticPayload || f.staticPayload || null,
          staticPayloadStr: (f.StaticPayload || f.staticPayload)
            ? (typeof (f.StaticPayload || f.staticPayload) === 'object'
              ? JSON.stringify(f.StaticPayload || f.staticPayload, null, 2)
              : String(f.StaticPayload || f.staticPayload))
            : '',
          options: Array.isArray(f.options) ? f.options : [],
          value: f.value ?? ''
        }))
        : []
    };
    const columns = cfg.list?.columns || {};
    this.tableRows = Object.keys(columns).map(key => {
      const col = columns[key] || {};
      const type: ColumnType = col.type ?? COLUMN_DEFAULTS['type'];
      const titleOverrideGroups = this.buildTitleOverrideGroupsFromMap(col.titleOverrides);

      const rawOptionsPayload = col.options?.payload ?? col.options?.staticPayload ?? col.options?.StaticPayload;
      const rawMappingPayload = col.mapping?.payload ?? col.mapping?.mappingPayload;
      const rawClickPayload = col.click?.payload;
      const rawPopupPayload = col.click?.popup?.payload;
      const rawRowKeys = col.click?.rowKeys || (col.click?.rowKey ? [col.click?.rowKey] : []);
      const rowKeysStr = Array.isArray(rawRowKeys) ? rawRowKeys.join(', ') : (typeof rawRowKeys === 'string' ? rawRowKeys : '');
      const confirmAction = !!(col.click?.confirmAction ?? col.click?.confirm);
      const warningMessage = col.click?.warningMessage ?? col.click?.confirmText ?? '';

      const colReadonly = col.readonly === true;
      const colReadonlyAdd = col.readonly_add !== undefined ? col.readonly_add === true : colReadonly;
      const colReadonlyEdit = col.readonly_edit !== undefined ? col.readonly_edit === true : colReadonly;

      return {
        payload: {
          id: col._id,
          key,
          title: col.title ?? COLUMN_DEFAULTS['title'],
          titleOverrides: col.titleOverrides ?? {},
          Order_By: col.Order_By ?? col.order_by ?? col.orderBy ?? col.order ?? '',
          show: col.show ?? COLUMN_DEFAULTS['show'],
          add: col.add ?? COLUMN_DEFAULTS['add'],
          edit: col.edit ?? COLUMN_DEFAULTS['edit'],
          view: col.view ?? COLUMN_DEFAULTS['view'],
          readonly: colReadonly || (colReadonlyAdd && colReadonlyEdit),
          readonly_add: colReadonlyAdd,
          readonly_edit: colReadonlyEdit,
          required: col.required ?? COLUMN_DEFAULTS['required'],
          display: col.display ?? COLUMN_DEFAULTS['display'],
          class: col.class ?? COLUMN_DEFAULTS['class'],
          regex: col.regex ?? COLUMN_DEFAULTS['regex'],
          regexMessage: col.regexMessage ?? COLUMN_DEFAULTS['regexMessage'],
          type,
          ignore_value: !!(col.ignore_value ?? col.ignoreValue),
          value: col.value ?? col.defaultValue ?? col.text ?? col.staticValue ?? '',
          formula: col.formula || col.expression || '',
          click: col.click
            ? {
              ...col.click,
              payload: rawClickPayload,
              payloadStr: this.formatPayloadStr(rawClickPayload),
              rowKeys: Array.isArray(rawRowKeys) ? rawRowKeys : [],
              rowKeysStr,
              confirmAction,
              warningMessage,
              popup: col.click.popup
                ? {
                  ...col.click.popup,
                  payload: rawPopupPayload,
                  payloadStr: this.formatPayloadStr(rawPopupPayload)
                }
                : undefined,
              fields: Array.isArray(col.click.fields)
                ? col.click.fields.map((f: any) => this.normalizeFormField(f))
                : []
            }
            : null,
          options: col.options
            ? {
              ...col.options,
              matchKey: col.options.matchKey ?? '',
              payload: rawOptionsPayload,
              payloadStr: this.formatPayloadStr(rawOptionsPayload),
              extraOption: col.options.extraOption
                ? {
                  id: col.options.extraOption.id ?? '',
                  name: col.options.extraOption.name ?? ''
                }
                : null
            }
            : null,
          mapping: col.mapping
            ? {
              ...col.mapping,
              payload: rawMappingPayload,
              payloadStr: this.formatPayloadStr(rawMappingPayload),
              json: (typeof col.mapping.json === 'string' && col.mapping.json.trim())
                ? JSON.parse(col.mapping.json)
                : (col.mapping.json ?? {})
            }
            : null,
          download: col.download ?? COLUMN_DEFAULTS['download'],
          total: col.total === true || (col as any).total === 'true',
          role: Array.isArray(col.role) ? col.role : [],
          role_add: Array.isArray(col.role_add) ? col.role_add : [],
          role_edit: Array.isArray(col.role_edit) ? col.role_edit : [],
          role_readonly: Array.isArray(col.role_readonly) ? col.role_readonly : [],
          role_readonly_add: Array.isArray(col.role_readonly_add) ? col.role_readonly_add : [],
          role_readonly_edit: Array.isArray(col.role_readonly_edit) ? col.role_readonly_edit : [],
          status: col.status ?? COLUMN_DEFAULTS['status'],
          conversionType: col.conversionType || col.bitConfig?.conversionType || 'decimal',
          bitCount: col.bitCount || col.bitConfig?.bitCount || 12,
          bitLabels: col.bitLabels || col.bitConfig?.bitLabels || [],
          bitLabelsStr: Array.isArray(col.bitLabels || col.bitConfig?.bitLabels)
            ? (col.bitLabels || col.bitConfig?.bitLabels).join(', ')
            : (col.bitLabelsStr || ''),
          minDate: col.minDate ?? col.min_date ?? '',
          maxDate: col.maxDate ?? col.max_date ?? '',
          monthYear: !!(col.monthYear ?? col.month_year)
        },
        optionsMode: type === 'dropdownUrl' ? 'api' : type === 'dropdownList' ? 'static' : 'none',
        expanded: false,
        mappingEntries: this.buildMappingEntriesFromJson(col.mapping?.json),
        titleOverrideEnabled: titleOverrideGroups.length > 0,
        titleOverrideGroups: titleOverrideGroups.length ? titleOverrideGroups : [{ roles: [], title: '' }]
      } as TableColumnRow;
    });
  }
  private buildTitleOverrideGroupsFromMap(map: Record<string, string> | undefined): TitleOverrideGroup[] {
    if (!map || Object.keys(map).length === 0) return [];
    const byTitle = new Map<string, string[]>();
    for (const [roleType, title] of Object.entries(map)) {
      if (!byTitle.has(title)) byTitle.set(title, []);
      byTitle.get(title)!.push(roleType);
    }
    return Array.from(byTitle.entries()).map(([title, roles]) => ({ title, roles }));
  }
  onTitleOverrideToggle(row: TableColumnRow, enabled: boolean): void {
    row.titleOverrideEnabled = enabled;
    if (enabled && row.titleOverrideGroups.length === 0) {
      row.titleOverrideGroups = [{ roles: [], title: '' }];
    }
    if (!enabled) {
      row.payload.titleOverrides = {};
    } else {
      this.syncTitleOverridesFromGroups(row);
    }
  }

  addTitleOverrideGroup(row: TableColumnRow): void {
    row.titleOverrideGroups.push({ roles: [], title: '' });
  }

  removeTitleOverrideGroup(row: TableColumnRow, index: number): void {
    row.titleOverrideGroups.splice(index, 1);
    this.syncTitleOverridesFromGroups(row);
  }

  isRoleInGroup(group: TitleOverrideGroup, roleType: string): boolean {
    return group.roles.includes(roleType);
  }

  toggleRoleInGroup(row: TableColumnRow, group: TitleOverrideGroup, roleType: string): void {
    if (group.roles.includes(roleType)) {
      group.roles = group.roles.filter(r => r !== roleType);
    } else {
      // a role can only sit in one group — pull it out of any other group first
      for (const g of row.titleOverrideGroups) {
        if (g !== group) g.roles = g.roles.filter(r => r !== roleType);
      }
      group.roles = [...group.roles, roleType];
    }
    this.syncTitleOverridesFromGroups(row);
  }

  onGroupTitleChange(row: TableColumnRow): void {
    this.syncTitleOverridesFromGroups(row);
  }

  unassignedRoles(row: TableColumnRow): RoleOption[] {
    const used = new Set(row.titleOverrideGroups.flatMap(g => g.roles));
    return this.roles.filter(r => !used.has(r.type));
  }

  assignAllRemainingToGroup(row: TableColumnRow, group: TitleOverrideGroup): void {
    const remaining = this.unassignedRoles(row).map(r => r.type);
    group.roles = [...group.roles, ...remaining];
    this.syncTitleOverridesFromGroups(row);
  }

  syncTitleOverridesFromGroups(row: TableColumnRow): void {
    const map: Record<string, string> = {};
    for (const group of row.titleOverrideGroups) {
      if (!group.title.trim()) continue;
      for (const roleType of group.roles) {
        map[roleType] = group.title;
      }
    }
    row.payload.titleOverrides = map;
  }
  private parseHeaderJson(text: string): any[] | null {
    if (!text.trim()) {
      this.headerJsonError = 'JSON is empty.';
      return null;
    }
    let raw: any;
    try {
      raw = JSON.parse(text);
    } catch (e: any) {
      this.headerJsonError = `Invalid JSON: ${e.message}`;
      return null;
    }
    // Accept either a bare array, or { "header": [...] } as pasted from a full response
    const arr = Array.isArray(raw) ? raw : raw?.header;
    if (!Array.isArray(arr)) {
      this.headerJsonError = 'Expected an array of { name, key, filter, show, click } entries (or an object containing a "header" array).';
      return null;
    }
    for (const item of arr) {
      if (!item?.key) {
        this.headerJsonError = 'Every entry must have a "key".';
        return null;
      }
    }
    return arr;
  }

  private findExistingRow(key: string): TableColumnRow | undefined {
    return this.tableRows.find(r => r.payload.key === key);
  }
  convertAndMergeHeaders(): void {
    this.headerJsonError = '';
    this.headerConvertWarnings = [];

    const headers = this.parseHeaderJson(this.headerJsonText);
    if (!headers) return;
    this.tableRows = this.tableRows.filter(r => !!r.payload.key);

    const warnings: string[] = [];

    for (const item of headers) {
      const key = item.key;
      const title = item.name ?? key;
      const show = item.show !== undefined ? !!item.show : true;

      let row = this.findExistingRow(key);
      if (row) {
        // Existing column — only refresh title/show, keep everything else
        // (add/edit/type/mapping/etc.) that's already been configured.
        row.payload.title = title;
        row.payload.show = show;
      } else {
        row = this.blankColumn(key, title);
        row.payload.show = show;
        this.tableRows.push(row);
      }

      if (item.click) {
        // Only set if not already configured — preserve any existing click config.
        if (!row.payload.click) {
          if (item.PopRedirect) {
            // Redirect case: navigates to a route, carries a payload map.
            row.payload.click = {
              type: 'redirect',
              redirectUrl: item.PopRedirect,
              payload: item.PopPayLoad ?? {}
            };
          } else if (item.PopUrl) {
            // Popup case: opens a modal pointed at contentUrl.
            row.payload.click = {
              type: 'popup',
              popup: {
                title,
                size: 'lg',
                contentUrl: `/${item.PopUrl}`
              }
            };
            if (item.PopPayLoad) {
              warnings.push(`"${key}" had a PopPayLoad alongside PopUrl — popup config doesn't carry a payload, review manually.`);
            }
          } else {
            // Old format only carries a boolean — no target at all. Default
            // to an empty redirect and flag it so it gets filled in manually.
            row.payload.click = { type: 'redirect', redirectUrl: '' };
            warnings.push(`"${key}" was marked clickable — set its redirect/popup target manually.`);
          }
        }
      }

      const isInlineMap = item.MapByKey !== null && typeof item.MapByKey === 'object';
      const hasUrlMapping = !!item.MapByUrl || (typeof item.MapByKey === 'string' && !!item.MapByKey) || !!item.MapByValue;

      if (isInlineMap || hasUrlMapping) {
        // Only set if not already configured — preserve any existing mapping.
        if (!row.payload.mapping) {
          row.payload.mapping = {
            mappingUrl: item.MapByUrl ? `/${item.MapByUrl}` : '',
            mappingKey: isInlineMap ? '' : (item.MapByKey ?? ''),
            mappingValue: isInlineMap ? '' : (item.MapByValue ?? ''),
            json: isInlineMap ? JSON.stringify(item.MapByKey, null, 2) : ''
          };
        }
      }

      if (Array.isArray(item.role) && item.role.length) {
        // Only set if not already configured — preserve any existing role list.
        if (!row.payload.role || !row.payload.role.length) {
          row.payload.role = [...item.role];
        }
      }
    }

    this.headerConvertWarnings = warnings;
    this.tableViewMode = 'form';
    this.toast.showToast('Header columns imported and merged successfully', 'colored', 5000, 'success');

  }
  // ---- Fetch existing config ----
  fetchTableConfig(): void {
    const node = this.currentHierarchyNode;
    if (!node) return;

    this.tableConfigLoading = true;
    this.tableConfigWarning = '';

    this.apiCallService.getTableConfig({ parent_id: node.id }).subscribe({
      next: (res: any) => {
        this.tableConfigLoading = false;
        const data = res?.Data ?? [];
        const doc = Array.isArray(data) ? data[0] : data;

        if (!doc) {
          this.tableConfigWarning = 'No saved configuration found for this table. Please select "New" to create one.';
          this.tableMeta = this.blankTableMeta();
          this.tableRows = [];
          this.originalColumns = [];
          this.originalTableRows = [];
          this.removedColumns = [];
          this.removedColumnsTracker = [];
          this.tableConfigId = '';
          this.isExistingTableConfig = false;
          return;
        }

        this.hydrateFromConfig(doc);
        this.isExistingTableConfig = true;
        const columns = doc.list?.columns || {};
        this.originalColumns = Object.keys(columns).map(key => ({
          key,
          title: columns[key]?.title || key
        }));
        this.originalTableRows = this.tableRows.map(r => JSON.parse(JSON.stringify(r)));
        this.removedColumns = [];
        this.removedColumnsTracker = [];
      },
      error: () => {
        this.tableConfigLoading = false;
        this.tableConfigWarning = 'Failed to fetch table configuration.';
        this.tableMeta = this.blankTableMeta();
        this.tableRows = [];
        this.originalColumns = [];
        this.originalTableRows = [];
        this.removedColumns = [];
        this.removedColumnsTracker = [];
        this.isExistingTableConfig = false;
      }
    });
  }

  // ---- View toggle (form <-> json) ----
  async setTableViewMode(mode: 'form' | 'json' | 'convert' | 'oldImport'): Promise<void> {
    // Leaving JSON view with unapplied edits — don't let it happen silently.
    if (this.tableViewMode === 'json' && mode !== 'json' && this.tableJsonText !== this.tableJsonSnapshot) {
      const result = await Swal.fire({
        title: 'Unapplied JSON Changes',
        html: `
          <p>Changes detected in JSON. Do you wish to apply them to the form?</p>
          <div style="font-size: 13px; color: #666; margin-top: 8px;">
            <strong>Apply changes</strong> and switch to Form view<br>
            <strong>Discard</strong> the unapplied JSON edits<br>
            <strong>Stay</strong> on JSON tab to keep editing
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Apply changes',
        denyButtonText: 'Discard edits',
        cancelButtonText: 'Stay on JSON',
        confirmButtonColor: '#3085d6',
        denyButtonColor: '#d33',
        cancelButtonColor: '#6c757d'
      });

      if (result.isDismissed) {
        return; // stay on JSON view
      }

      if (result.isConfirmed) {
        const parsed = this.parseTableJson(this.tableJsonText);
        if (parsed === null) {
          // invalid JSON — keep them on the JSON tab so they can fix it
          return;
        }
        this.hydrateFromConfig(parsed);
        this.tableJsonError = '';
        this.tableJsonSnapshot = this.tableJsonText;
        this.toast.showToast('JSON changes applied to form successfully', 'colored', 5000, 'success');
        this.tableViewMode = 'form';
        return;
      }
      // Denied = discard edits, fall through to whatever mode they picked
    }

    if (mode === 'json') {
      this.tableJsonText = this.serializeTableJson();
      this.tableJsonSnapshot = this.tableJsonText;
      this.tableJsonError = '';
      this.tableViewMode = 'json';
      return;
    }

    if (mode === 'convert') {
      this.headerJsonError = '';
      this.headerConvertWarnings = [];
      this.tableViewMode = 'convert';
      return;
    }
    if (mode === 'oldImport') {
      this.oldFormatJsonError = '';
      this.oldFormatConvertWarnings = [];
      this.tableViewMode = 'oldImport';
      return;
    }

    this.tableViewMode = mode;
  }

  applyTableJson(): void {
    const parsed = this.parseTableJson(this.tableJsonText);
    if (parsed === null) return;
    this.hydrateFromConfig(parsed);
    this.tableJsonError = '';
    this.tableJsonSnapshot = this.tableJsonText;
    this.tableViewMode = 'form';
    this.toast.showToast('JSON changes applied to form successfully', 'colored', 5000, 'success');
  }

  refreshTableJsonFromForm(): void {
    this.tableJsonText = this.serializeTableJson();
    this.tableJsonSnapshot = this.tableJsonText;
    this.tableJsonError = '';
  }

  private serializeTableJson(): string {
    return JSON.stringify(this.buildTablePayload(), null, 2);
  }

  private parseTableJson(text: string): any | null {
    if (!text.trim()) {
      this.tableJsonError = 'JSON is empty.';
      return null;
    }

    let raw: any;
    try {
      raw = JSON.parse(text);
    } catch (e: any) {
      this.tableJsonError = `Invalid JSON: ${e.message}`;
      return null;
    }

    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      this.tableJsonError = 'Root must be a single config object, not an array.';
      return null;
    }

    if (!raw.list || typeof raw.list !== 'object') {
      this.tableJsonError = 'Missing "list" object.';
      return null;
    }

    if (raw.list.columns !== undefined && (typeof raw.list.columns !== 'object' || Array.isArray(raw.list.columns))) {
      this.tableJsonError = '"list.columns" must be an object keyed by column key.';
      return null;
    }

    return raw;
  }

  // HTML Form Importer State
  showHtmlImportModal = false;
  htmlImportRawText = '';
  htmlImportTargetClick: ClickConfig | null = null;
  htmlImportTargetFields: FieldConfig[] | null = null;
  htmlImportTargetMode: 'columnForm' | 'modalForm' | 'form' = 'columnForm';
  parsedImportTitle = '';
  parsedImportApiKey = '';
  parsedImportFields: (FieldConfig & { selected: boolean; originalType?: string })[] = [];
  htmlImportReplace = true;

  openHtmlImportModal(target: ClickConfig | FieldConfig[], mode: 'columnForm' | 'modalForm' | 'form' = 'columnForm'): void {
    if (Array.isArray(target)) {
      this.htmlImportTargetFields = target;
      this.htmlImportTargetClick = null;
    } else {
      this.htmlImportTargetClick = target;
      this.htmlImportTargetFields = this.getColumnFormFields(target);
    }
    this.htmlImportTargetMode = mode;
    this.htmlImportRawText = '';
    this.parsedImportTitle = '';
    this.parsedImportApiKey = '';
    this.parsedImportFields = [];
    this.htmlImportReplace = true;
    this.showHtmlImportModal = true;
  }

  closeHtmlImportModal(): void {
    this.showHtmlImportModal = false;
  }

  parseHtmlFormSnippet(): void {
    if (!this.htmlImportRawText || !this.htmlImportRawText.trim()) {
      this.toast.showToast('Please paste an HTML snippet first', 'colored', 3000, 'warning');
      return;
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(this.htmlImportRawText, 'text/html');

      // 1. Extract Dialog / Form Title
      let title = '';
      const titleEl = doc.querySelector('.ui-dialog-title, .modal-title, .card-title, h1, h2, h3, h4, legend, title');
      if (titleEl && titleEl.textContent) {
        title = titleEl.textContent.trim();
      }
      this.parsedImportTitle = title;

      // 2. Extract API Key / Action from Submit Button
      let apiKey = '';
      const buttons = Array.from(doc.querySelectorAll('.ui-dialog-buttonset button, .modal-footer button, form button, input[type="submit"]'));
      for (const btn of buttons) {
        const text = (btn.textContent || (btn as HTMLInputElement).value || '').trim();
        const lower = text.toLowerCase();
        if (text && !['cancel', 'close', 'reset', 'dismiss', 'back'].includes(lower)) {
          apiKey = text;
          break;
        }
      }
      this.parsedImportApiKey = apiKey;

      // 3. Extract Form Controls
      const elements = Array.from(doc.querySelectorAll('input, select, textarea'));
      const parsed: (FieldConfig & { selected: boolean; originalType?: string })[] = [];
      const existingTableKeys = new Set(this.getExistingKeysList());
      const seenIds = new Set<string>();

      elements.forEach((el) => {
        const tag = el.tagName.toLowerCase();
        const inputType = (el.getAttribute('type') || 'text').toLowerCase();

        // Skip buttons
        if (['button', 'submit', 'reset', 'image'].includes(inputType)) return;

        const rawId = el.getAttribute('id') || el.getAttribute('name') || '';
        if (!rawId && !el.getAttribute('value') && !el.closest('tr')) return;

        const id = rawId || `field_${parsed.length + 1}`;
        if (seenIds.has(id)) return;
        seenIds.add(id);

        // Find label / placeholder
        let label = '';
        if (rawId) {
          const labelEl = doc.querySelector(`label[for="${rawId}"]`);
          if (labelEl && labelEl.textContent) {
            label = labelEl.textContent.trim();
          }
        }
        if (!label) {
          const parentLabel = el.closest('label');
          if (parentLabel && parentLabel.textContent) {
            label = parentLabel.textContent.replace(el.textContent || '', '').trim();
          }
        }
        if (!label && el.closest('tr')) {
          const tr = el.closest('tr');
          const td = el.closest('td');
          if (tr && td) {
            const tds = Array.from(tr.querySelectorAll('td, th'));
            const idx = tds.indexOf(td);
            if (idx > 0 && tds[idx - 1].textContent) {
              label = tds[idx - 1].textContent?.trim() || '';
            }
          }
        }
        if (!label) {
          label = el.getAttribute('placeholder') || el.getAttribute('title') || '';
        }
        // Clean label trailing colons / asterisks
        label = label.replace(/[:*]+$/, '').trim();
        if (!label) {
          label = this.formatHeaderName(id);
        }

        // Determine field type
        let type: ColumnType = 'text';
        let isDropdown = false;
        let options: StaticOption[] = [{ id: '', name: '' }];
        let optionsMode: OptionsMode = 'none';
        let view = true;

        if (tag === 'select') {
          type = 'dropdownList';
          isDropdown = true;
          optionsMode = 'static';
          const optEls = Array.from(el.querySelectorAll('option'));
          if (optEls.length) {
            options = optEls.map(o => ({
              id: o.getAttribute('value') ?? o.textContent?.trim() ?? '',
              name: o.textContent?.trim() ?? o.getAttribute('value') ?? ''
            }));
          }
        } else if (inputType === 'hidden') {
          type = 'text';
          view = false;
        } else if (inputType === 'number') {
          type = 'number';
        } else if (inputType === 'date' || inputType === 'datetime-local') {
          type = 'date';
        }

        const isReadonly = el.hasAttribute('readonly') || el.getAttribute('readonly') === 'readonly' || el.hasAttribute('disabled');
        const isRequired = el.hasAttribute('required');

        // Auto-detect fillFromKey if id matches an existing table column key
        const fillFromKey = existingTableKeys.has(id) ? id : '';

        parsed.push({
          id,
          placeholder: label,
          type,
          class: 'col-md-6',
          dropdown: isDropdown,
          apiUrl: '',
          optionKey: 'id',
          option_mapping: { id: 'id', name: 'name' },
          regex: '',
          regexMessage: '',
          required: isRequired,
          readonly: isReadonly,
          view,
          options,
          optionsMode,
          expanded: false,
          conversionType: 'decimal',
          bitCount: 12,
          bitLabels: [],
          bitLabelsStr: '',
          fillFromKey,
          conditionEnabled: false,
          conditionKey: '',
          conditionOperator: '==',
          conditionValue: '',
          selected: true,
          originalType: inputType
        });
      });

      this.parsedImportFields = parsed;
      if (parsed.length === 0) {
        this.toast.showToast('No form fields detected in the pasted HTML', 'colored', 3500, 'warning');
      } else {
        this.toast.showToast(`Found ${parsed.length} form fields ready to import`, 'colored', 3000, 'success');
      }
    } catch (err: any) {
      console.error('HTML Form Parsing Error:', err);
      this.toast.showToast('Failed to parse HTML: ' + (err?.message || err), 'colored', 4000, 'danger');
    }
  }

  selectAllImportFields(select: boolean): void {
    this.parsedImportFields.forEach(f => f.selected = select);
  }

  applyHtmlImport(): void {
    const selectedFields = this.parsedImportFields.filter(f => f.selected);
    if (!selectedFields.length) {
      this.toast.showToast('Please select at least one field to import', 'colored', 3000, 'warning');
      return;
    }

    if (this.htmlImportTargetClick && this.parsedImportApiKey && !this.htmlImportTargetClick.apiKey) {
      this.htmlImportTargetClick.apiKey = this.parsedImportApiKey;
    }

    if (this.htmlImportTargetClick && this.parsedImportTitle && this.htmlImportTargetClick.popup) {
      this.htmlImportTargetClick.popup.title = this.parsedImportTitle;
    }

    const fieldsToApply: FieldConfig[] = selectedFields.map(f => {
      const { selected, originalType, ...cleanField } = f;
      return cleanField;
    });

    if (this.htmlImportTargetFields) {
      if (this.htmlImportReplace) {
        this.htmlImportTargetFields.length = 0;
        this.htmlImportTargetFields.push(...fieldsToApply);
      } else {
        this.htmlImportTargetFields.push(...fieldsToApply);
      }
    }

    this.toast.showToast(`Successfully imported ${fieldsToApply.length} form fields!`, 'colored', 4000, 'success');
    this.closeHtmlImportModal();
  }

  private formatHeaderName(name: string): string {
    return (name || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .trim();
  }

  // ---- Shared field-editor helpers (Form / ModalForm) ----
  addFieldTo(fields: FieldConfig[]): void {
    fields.push(this.blankField());
  }

  removeFieldFrom(fields: FieldConfig[], index: number): void {
    fields.splice(index, 1);
  }

  addOption(field: FieldConfig): void {
    this.addStaticOptionToField(field);
  }

  removeOption(field: FieldConfig, index: number): void {
    this.removeStaticOptionFromField(field, index);
  }

  addConditionRule(field: FieldConfig): void {
    if (!field.conditions) field.conditions = [];
    field.conditions.push({ key: '', operator: '==', value: '', logic: 'OR' });
  }

  removeConditionRule(field: FieldConfig, index: number): void {
    if (!field.conditions) return;
    field.conditions.splice(index, 1);
    if (field.conditions.length === 0) {
      field.conditions.push({ key: '', operator: '==', value: '', logic: 'AND' });
    }
  }

  private blankField(): FieldConfig {
    return {
      id: '',
      placeholder: '',
      type: 'text',
      class: 'col-md-6',
      dropdown: false,
      apiUrl: '',
      optionKey: 'id',
      option_mapping: { id: 'id', name: 'name' },
      regex: '',
      regexMessage: '',
      required: false,
      readonly: false,
      view: true,
      role: [],
      options: [{ id: '', name: '' }],
      optionsMode: 'none',
      expanded: true,
      conversionType: 'decimal',
      bitCount: 12,
      bitLabels: [],
      bitLabelsStr: '',
      minBase: '',
      maxBase: '',
      baseValue: '',
      bitDirection: 'ltr',
      fillFromKey: '',
      minDate: '',
      maxDate: '',
      monthYear: false,
      conditionEnabled: false,
      conditionKey: '',
      conditionOperator: '==',
      conditionValue: '',
      conditions: [{ key: '', operator: '==', value: '', logic: 'AND' }],
      conditionLogic: 'AND'
    };
  }

  private normalizeFormField(f: any): FieldConfig {
    const isDropdownUrl = f.type === 'dropdownUrl' || (f.dropdown && !!f.apiUrl);
    const isDropdownList = f.type === 'dropdownList' || (f.dropdown && !f.apiUrl);
    const type: ColumnType = f.type || (isDropdownUrl ? 'dropdownUrl' : isDropdownList ? 'dropdownList' : 'text');
    const rawPayload = f.payload ?? f.StaticPayload ?? null;

    return {
      id: f.id || f.key || '',
      placeholder: f.placeholder || f.title || f.name || f.label || '',
      type,
      class: f.class || 'col-md-6',
      dropdown: isDropdownUrl || isDropdownList || !!f.dropdown,
      apiUrl: f.apiUrl || f.url || '',
      payload: rawPayload,
      payloadStr: this.formatPayloadStr(rawPayload),
      optionKey: f.optionKey || f.option_mapping?.id || 'id',
      option_mapping: {
        id: f.option_mapping?.id || f.optionKey || 'id',
        name: f.option_mapping?.name || 'name'
      },
      matchKey: f.matchKey || f.options?.matchKey || '',
      options: Array.isArray(f.options) && f.options.length
        ? f.options.map((o: any) => ({ id: o.id ?? o.value ?? '', name: o.name ?? o.label ?? '' }))
        : [{ id: '', name: '' }],
      extraOption: f.extraOption
        ? { id: f.extraOption.id ?? '', name: f.extraOption.name ?? '' }
        : (f.mapping?.json && typeof f.mapping.json === 'object' && Object.keys(f.mapping.json).length
          ? { id: Object.keys(f.mapping.json)[0], name: String(Object.values(f.mapping.json)[0]) }
          : null),
      regex: f.regex || '',
      regexMessage: f.regexMessage || '',
      required: !!f.required,
      readonly: !!f.readonly,
      view: f.view !== false,
      role: Array.isArray(f.role) ? f.role : [],
      optionsMode: isDropdownUrl ? 'api' : isDropdownList ? 'static' : 'none',
      expanded: false,
      conversionType: f.conversionType || f.bitConfig?.conversionType || 'decimal',
      bitCount: f.bitCount || f.bitConfig?.bitCount || 12,
      bitLabels: f.bitLabels || f.bitConfig?.bitLabels || [],
      bitLabelsStr: Array.isArray(f.bitLabels || f.bitConfig?.bitLabels)
        ? (f.bitLabels || f.bitConfig?.bitLabels).join(', ')
        : (f.bitLabelsStr || ''),
      minBase: f.minBase !== undefined ? f.minBase : (f.bitConfig?.minBase ?? ''),
      maxBase: f.maxBase !== undefined ? f.maxBase : (f.bitConfig?.maxBase ?? ''),
      baseValue: f.baseValue !== undefined ? f.baseValue : (f.bitConfig?.baseValue ?? ''),
      bitDirection: f.bitDirection || f.bitConfig?.bitDirection || 'ltr',
      fillFromKey: f.fillFromKey || f.sourceKey || f.rowKey || '',
      minDate: f.minDate ?? f.min_date ?? '',
      maxDate: f.maxDate ?? f.max_date ?? '',
      monthYear: !!(f.monthYear ?? f.month_year),
      conditionEnabled: f.conditionEnabled === true,
      conditionKey: f.conditionKey || '',
      conditionOperator: f.conditionOperator || '==',
      conditionValue: f.conditionValue !== undefined ? String(f.conditionValue) : '',
      conditions: Array.isArray(f.conditions) && f.conditions.length
        ? f.conditions.map((c: any) => ({
            key: c.key || '',
            operator: c.operator || '==',
            value: c.value !== undefined ? String(c.value) : '',
            logic: c.logic || 'AND'
          }))
        : (f.conditionKey ? [{ key: f.conditionKey, operator: f.conditionOperator || '==', value: f.conditionValue !== undefined ? String(f.conditionValue) : '', logic: 'AND' }] : [{ key: '', operator: '==', value: '', logic: 'AND' }]),
      conditionLogic: f.conditionLogic || 'AND'
    };
  }

  // ---- Report config helper ----
  blankReportConfig(): any {
    return {
      header: 'Untitled Report',
      reportType: '',
      apiUrl: 'Reports',
      reports: true,
      consumer: false,
      Date_Flat: false,
      AllSelection: false,
      showDaterange: true,
      showSingleDate: false,
      Month_Year: false,
      Date_Time: false,
      SiteOnly: false,
      showLocation: true,
      SingleLocationDropdown: false,
      ShowLocationDropdown: false,
      showSites: false,
      showsubtype: false,
      showBillNo: false,
      Hours: false
    };
  }

  // ---- Log config helper ----
  blankLogConfig(): any {
    return {
      header: 'Untitled Log',
      logKey: '',
      orderBy: 'creation_time DESC',
      downloadHeader: '',
      showLocation: false,
      showDaterange: true,
      DateTimerange: false,
      timeDuration: 0,
      showSingleDate: false,
      AllSelection: false,
      showappliances: false,
      FromToLocation: false,
      tableLocation: false,
      filterConfig: []
    };
  }

  // ---- Save ----
  private stampCreationMeta(payload: any): void {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    payload.creation_time =
      `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
      `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
    payload.creation_time_ms = now.getTime();
    payload.created_by = localStorage.getItem('loginId') || 'unknown';
  }

  private createNewTableConfig(): void {
    const payload = this.buildTablePayload();
    this.stampCreationMeta(payload);

    this.apiCallService.setTableConfig(payload).subscribe({
      next: (res: any) => {
        if (res?.Status === 'Success') {
          this.tableConfigWarning = '';
          this.toast.showToast('Table Created Successfully', 'colored', 5000, 'success');

          // The create response may not reliably carry the new _id (or the
          // per-column _ids), so re-fetch the config from the server. This
          // hydrates tableConfigId + isExistingTableConfig properly so the
          // very next save goes through updateExistingTableConfig() instead
          // of creating a duplicate.
          this.fetchTableConfig();
        } else {
          this.tableConfigWarning = res?.message || 'Failed to save table configuration.';
          this.toast.showToast(this.tableConfigWarning, 'colored', 5000, 'danger');
        }
      },
      error: () => {
        this.tableConfigWarning = 'Failed to save table configuration.';
        this.toast.showToast(this.tableConfigWarning, 'colored', 5000, 'danger');
      }
    });
  }

  private updateExistingTableConfig(): void {
    const payload = [this.buildTablePayload()];

    this.apiCallService.putTableConfig(payload).subscribe({
      next: (res: any) => {
        if (res?.Status === 'Success') {
          this.tableConfigWarning = '';
          this.toast.showToast('Table Updated Successfully', 'colored', 5000, 'success');
          this.originalColumns = this.tableRows
            .filter(r => !!r.payload.key)
            .map(r => ({ key: r.payload.key, title: r.payload.title || r.payload.key }));
          this.originalTableRows = this.tableRows.map(r => JSON.parse(JSON.stringify(r)));
          this.removedColumns = [];
          this.removedColumnsTracker = [];
        } else {
          this.tableConfigWarning = res?.message || 'Failed to update table configuration.';
          this.toast.showToast(this.tableConfigWarning, 'colored', 5000, 'danger');
        }
      },
      error: () => {
        this.tableConfigWarning = 'Failed to update table configuration.';
        this.toast.showToast(this.tableConfigWarning, 'colored', 5000, 'danger');
      }
    });
  }

  private escapeHtml(str: string): string {
    return (str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private getRemovedColumns(): { key: string; title: string }[] {
    const currentKeys = new Set(
      this.tableRows
        .map(r => r.payload?.key?.trim())
        .filter((k): k is string => !!k)
    );

    const removedMap = new Map<string, string>(); // identifier -> title

    // 1. Check original columns loaded from backend
    for (const col of this.originalColumns) {
      if (col.key && !currentKeys.has(col.key)) {
        removedMap.set(col.key, col.title || col.key);
      }
    }

    // 2. Check explicitly tracked columns removed in UI
    for (const col of this.removedColumnsTracker) {
      if (col.key) {
        if (!currentKeys.has(col.key)) {
          removedMap.set(col.key, col.title || col.key);
        }
      } else if (col.title && col.title.trim()) {
        removedMap.set(`__title_${col.title}`, col.title);
      }
    }

    return Array.from(removedMap.entries()).map(([k, title]) => ({
      key: k.startsWith('__title_') ? '' : k,
      title
    }));
  }

  async saveForm(): Promise<void> {
    if (this.componentType === 'table' && this.tableViewMode === 'json' && this.tableJsonText !== this.tableJsonSnapshot) {
      const result = await Swal.fire({
        title: 'Unapplied JSON Changes',
        html: `
          <p>You have unapplied JSON changes. Apply them before saving?</p>
          <div style="font-size: 13px; color: #666; margin-top: 8px;">
            <strong>Apply &amp; continue saving</strong><br>
            <strong>Save without JSON changes</strong><br>
            <strong>Cancel</strong> to abort saving
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Apply & continue saving',
        denyButtonText: 'Save without JSON changes',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3085d6',
        denyButtonColor: '#f0ad4e',
        cancelButtonColor: '#6c757d'
      });

      if (result.isDismissed) {
        return; // Abort save
      }

      if (result.isConfirmed) {
        const parsed = this.parseTableJson(this.tableJsonText);
        if (parsed === null) {
          this.toast.showToast('Cannot save — fix the JSON error first', 'colored', 5000, 'danger');
          return;
        }
        this.hydrateFromConfig(parsed);
        this.tableJsonSnapshot = this.tableJsonText;
        this.tableViewMode = 'form';
      }
      // Denied = save without these JSON changes
    }

    if (!this.canSave) return;

    if (this.componentType === 'table') {
      if (this.basis === 'api' && this.tableRows.length === 0) {
        this.tableConfigWarning = 'No columns to save. Please select "New" or add columns first.';
        return;
      }

      // Check if some columns are removed
      const removedColumns = this.getRemovedColumns();
      if (removedColumns.length > 0) {
        const columnNamesHtml = removedColumns
          .map(c => `<li><strong>${this.escapeHtml(c.title || c.key)}</strong>${c.key && c.title && c.key !== c.title ? ` <span style="color: #666; font-weight: normal;">(${this.escapeHtml(c.key)})</span>` : ''}</li>`)
          .join('');

        const confirmResult = await Swal.fire({
          title: 'Columns Removed',
          html: `
            <div style="text-align: left; font-size: 14px;">
              <p>The following column(s) have been removed and will be deleted permanently:</p>
              <ul style="max-height: 180px; overflow-y: auto; margin: 10px 0; padding-left: 24px; color: #d33;">
                ${columnNamesHtml}
              </ul>
              <p style="margin-top: 14px; margin-bottom: 0;"><strong>Do you wish to save?</strong></p>
            </div>
          `,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, save',
          cancelButtonText: 'No, cancel',
          confirmButtonColor: '#d33',
          cancelButtonColor: '#6c757d'
        });

        if (!confirmResult.isConfirmed) {
          // User decided not to save after removing columns -> restore them back to their original positions
          const currentKeys = new Set(this.tableRows.map(r => r.payload?.key).filter(Boolean));

          // 1. Restore UI-removed columns in reverse order (LIFO) to preserve exact original indices
          while (this.removedColumns.length > 0) {
            const item = this.removedColumns.pop()!;
            if (!item.row.payload?.key || !currentKeys.has(item.row.payload.key)) {
              const targetIndex = Math.min(Math.max(0, item.index), this.tableRows.length);
              this.tableRows.splice(targetIndex, 0, item.row);
              if (item.row.payload?.key) currentKeys.add(item.row.payload.key);
            }
          }

          // 2. Restore any original columns missing due to JSON edits at their original position
          this.originalTableRows.forEach((origRow, origIndex) => {
            if (origRow.payload?.key && !currentKeys.has(origRow.payload.key)) {
              const targetIndex = Math.min(Math.max(0, origIndex), this.tableRows.length);
              this.tableRows.splice(targetIndex, 0, JSON.parse(JSON.stringify(origRow)));
              currentKeys.add(origRow.payload.key);
            }
          });

          this.removedColumns = [];
          this.removedColumnsTracker = [];

          if (this.tableViewMode === 'json') {
            this.tableJsonText = this.serializeTableJson();
            this.tableJsonSnapshot = this.tableJsonText;
          }

          this.toast.showToast('Removed Columns recovered', 'colored', 5000, 'success');
          return;
        }
      }

      if (this.basis === 'new') {
        this.createNewTableConfig();
        return;
      }

      if (this.isExistingTableConfig) {
        this.updateExistingTableConfig();
      } else {
        this.createNewTableConfig();
      }
      return;
    }

    const payload: any = {
      module: { id: this.selectedModuleId, name: this.selectedModule?.name },
      subModule: this.selectedSubModuleId
        ? { id: this.selectedSubModuleId, name: this.selectedSubModule?.name }
        : null,
      innerModule: this.selectedInnerModuleId
        ? { id: this.selectedInnerModuleId, name: this.selectedInnerModule?.name }
        : null,
      componentType: this.componentType,
      basis: this.basis
    };

    if (this.componentType === 'form') {
      payload.form = { fields: this.serializeFields(this.formFields) };
    } else if (this.componentType === 'modalform') {
      payload.modalForm = {
        modalTitle: this.modalForm.modalTitle,
        modalSize: this.modalForm.modalSize,
        fields: this.serializeFields(this.modalForm.fields)
      };
    }

    this.downloadJson(payload, 'component-config');
  }

  private serializeFields(fields: FieldConfig[]): any[] {
    return (fields || []).map(field => {
      const isDropdownUrl = field.type === 'dropdownUrl' || (field.dropdown && !!field.apiUrl);
      const isDropdownList = field.type === 'dropdownList' || (field.dropdown && !field.apiUrl && (field.options?.length || 0) > 0);
      const isDropdown = isDropdownUrl || isDropdownList;
      const type = field.type || (isDropdown ? (isDropdownUrl ? 'dropdownUrl' : 'dropdownList') : 'text');

      const res: any = {
        id: field.id || '',
        placeholder: field.placeholder || '',
        type,
        class: field.class || 'col-md-6',
        dropdown: isDropdown
      };

      if (field.required) res.required = true;
      if (field.readonly) res.readonly = true;
      if (field.view === false) res.view = false;
      if (Array.isArray(field.role) && field.role.length) res.role = field.role;
      if (field.regex) res.regex = field.regex;
      if (field.regexMessage) res.regexMessage = field.regexMessage;

      if (isDropdownUrl) {
        res.apiUrl = field.apiUrl || '';
        if (field.payloadStr && field.payloadStr.trim()) {
          res.payload = this.parsePayload(field.payloadStr);
        } else if (field.payload && typeof field.payload === 'object' && Object.keys(field.payload).length) {
          res.payload = field.payload;
        }
        res.optionKey = field.optionKey || field.option_mapping?.id || 'id';
        res.option_mapping = {
          id: field.option_mapping?.id || field.optionKey || 'id',
          name: field.option_mapping?.name || 'name'
        };
        if (field.matchKey && field.matchKey.trim()) {
          res.matchKey = field.matchKey.trim();
        }
        if (field.extraOption) {
          const extraId = String(field.extraOption.id ?? '').trim();
          const extraName = String(field.extraOption.name ?? '').trim();
          if (extraId || extraName) {
            res.extraOption = {
              id: extraId || 'ALL',
              name: extraName || 'All Units'
            };
          }
        }
      } else if (isDropdownList) {
        res.options = (field.options || []).map(opt => ({
          id: opt.id ?? '',
          name: opt.name ?? ''
        }));
        res.optionKey = field.optionKey || 'id';
        res.option_mapping = {
          id: 'id',
          name: 'name'
        };
      }

      if (type === 'bitcase') {
        res.conversionType = field.conversionType || 'decimal';
        res.bitCount = Number(field.bitCount) || 12;
        if (field.bitLabelsStr) {
          res.bitLabels = field.bitLabelsStr.split(',').map((s: string) => s.trim()).filter(Boolean);
        } else if (Array.isArray(field.bitLabels) && field.bitLabels.length) {
          res.bitLabels = field.bitLabels;
        }
        if (field.minBase !== undefined && field.minBase !== null && String(field.minBase).trim() !== '') {
          res.minBase = isNaN(Number(field.minBase)) ? field.minBase : Number(field.minBase);
        }
        if (field.maxBase !== undefined && field.maxBase !== null && String(field.maxBase).trim() !== '') {
          res.maxBase = isNaN(Number(field.maxBase)) ? field.maxBase : Number(field.maxBase);
        }
        if (field.baseValue !== undefined && field.baseValue !== null && String(field.baseValue).trim() !== '') {
          res.baseValue = isNaN(Number(field.baseValue)) ? field.baseValue : Number(field.baseValue);
        }
        res.bitDirection = field.bitDirection || 'ltr';
      }

      if (type === 'date') {
        if (field.minDate && field.minDate.trim()) {
          res.minDate = field.minDate.trim();
        }
        if (field.maxDate && field.maxDate.trim()) {
          res.maxDate = field.maxDate.trim();
        }
        if (field.monthYear) {
          res.monthYear = true;
        }
      }

      if (field.fillFromKey && field.fillFromKey.trim()) {
        res.fillFromKey = field.fillFromKey.trim();
      }
      if (field.conditionEnabled) {
        res.conditionEnabled = true;
        const validRules = (field.conditions || []).filter((c: any) => c && c.key && c.key.trim());
        if (validRules.length) {
          res.conditions = validRules.map((c: any) => ({
            key: c.key.trim(),
            operator: c.operator || '==',
            value: (c.value !== undefined ? String(c.value) : '').trim(),
            logic: c.logic || 'AND'
          }));
          res.conditionKey = validRules[0].key.trim();
          res.conditionOperator = validRules[0].operator || '==';
          res.conditionValue = (validRules[0].value !== undefined ? String(validRules[0].value) : '').trim();
        } else if (field.conditionKey) {
          res.conditionKey = (field.conditionKey || '').trim();
          res.conditionOperator = field.conditionOperator || '==';
          res.conditionValue = (field.conditionValue || '').trim();
          res.conditions = [{
            key: res.conditionKey,
            operator: res.conditionOperator,
            value: res.conditionValue,
            logic: 'AND'
          }];
        }
        res.conditionLogic = field.conditionLogic || 'AND';
      }

      return res;
    });
  }

  private downloadJson(data: any, filenamePrefix: string): void {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenamePrefix}-${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}

